import type { WebsiteLayout, WebsiteTemplate } from '@sweetspot/shared';
import { ApiError, dispatchUpgradeRequired } from './upgrade-wall';
import type { InstagramConversationsPage, InstagramThreadPage, InstagramFailurePage } from './instagram-inbox-types';
import { readInstagramMenuDraft, readInstagramMenuDraftPage, readInstagramMenuMediaPage, type InstagramMenuDraft } from './instagram-menu-drafts';
import { commentUuid, commentChannel, readOwnerInstagramComments, readInstagramCommentReplies, readInstagramCommentReply,
  readInstagramCommentPublication, readInstagramCommentRefresh, readInstagramCommentFailures,
  readInstagramCommentRetry, type CommentChannel, type CommentPublication } from './instagram-comments';

const API_URL =
  typeof window !== 'undefined'
    ? '/api/proxy'
    : process.env.NEXT_PUBLIC_API_URL || 'http://localhost:4000';

const STORAGE_KEY = 'sweetspot_current_business_id';
const AUTH_HEADER_TIMEOUT_MS = 3000;
const AUTH_HEADER_CACHE_MS = 15000;
const AUTH_HEADER_EMPTY_CACHE_MS = 400;
const UNREAD_COUNT_CACHE_MS = 5000;

let authHeadersCache: { value: Record<string, string>; expiresAt: number } | null = null;
let authHeadersInFlight: Promise<Record<string, string>> | null = null;
let supabaseClientPromise: Promise<any> | null = null;
let unreadCountCache: { key: string; value: { count: number }; expiresAt: number } | null = null;
let unreadCountInFlight: { key: string; promise: Promise<{ count: number }> } | null = null;

function getBusinessIdHeader(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const businessId = localStorage.getItem(STORAGE_KEY);
  if (!businessId) return {};
  return { 'X-Business-Id': businessId };
}

export const IMPERSONATION_STORAGE_KEY = 'sweetspot_impersonation';
export type ImpersonationMode = 'read_only' | 'support_write';

export interface ImpersonationSession {
  token: string;
  expiresAt: number;
  /** Missing on pre-support-mode sessions, which the API treats as read-only. */
  mode?: ImpersonationMode;
  user: { id: string; email: string | null; name: string | null };
}

export function getImpersonation(): ImpersonationSession | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(IMPERSONATION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ImpersonationSession;
    // On lapse run the full teardown, not just the token removal. The business
    // id in storage belongs to the *target*; leaving it behind means the app
    // keeps sending it under the admin's own identity, which resolves to no
    // membership and silently falls back to the admin's first business — the
    // screen still shows the target's data while every new request answers for
    // someone else.
    if (!parsed?.token || !parsed.expiresAt || parsed.expiresAt <= Date.now()) {
      clearImpersonationState();
      return null;
    }
    // Validate the shape too: `user.id` keys every GET cache entry and
    // `user.name` renders in the banner, both unguarded. A truncated or
    // older-schema value would throw a TypeError inside apiFetch for every
    // request — the browser-side twin of the lockout the token module hardens
    // against on the server.
    if (!parsed.user || typeof parsed.user.id !== 'string') {
      clearImpersonationState();
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export const IMPERSONATION_ENDED_EVENT = 'sweetspot:impersonation-ended';

function clearImpersonationState(): void {
  localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
  // The business switcher and every cached GET belong to the previous identity.
  localStorage.removeItem(STORAGE_KEY);
  getCache.clear();
  // Tell the banner now. Expiry is noticed lazily by whichever apiFetch runs
  // first; without this the header and business id are already gone — requests
  // resolve as the admin again — while the banner still shows the target until
  // its next poll. A click in that window could act on the admin's own account.
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(IMPERSONATION_ENDED_EVENT));
  }
}

export function setImpersonation(session: ImpersonationSession | null): void {
  if (typeof window === 'undefined') return;
  if (session) {
    localStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(session));
    localStorage.removeItem(STORAGE_KEY);
    getCache.clear();
  } else {
    clearImpersonationState();
  }
}

/**
 * Must go on EVERY authenticated request, not just the JSON ones.
 *
 * Without it the server resolves the admin, not the target, and every
 * ownership check downstream asks the wrong question. The product upload also
 * sends `X-Business-Id` for the target's business, which the admin holds no
 * membership in, so `resolveBusinessId` falls back to the admin's own first
 * business and the product is looked up under the wrong account entirely.
 * Either way the write is refused — which is how a support session ends up
 * unable to change a photo it is looking straight at.
 */
function getImpersonationHeader(): Record<string, string> {
  const session = getImpersonation();
  return session ? { 'X-Impersonate-Token': session.token } : {};
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  if (typeof window === 'undefined') return {};
  if (!process.env.NEXT_PUBLIC_SUPABASE_URL) return {};
  if (authHeadersCache && authHeadersCache.expiresAt > Date.now()) {
    return authHeadersCache.value;
  }
  if (authHeadersInFlight) return authHeadersInFlight;

  authHeadersInFlight = (async () => {
    try {
      const result = await Promise.race([
        (async () => {
          if (!supabaseClientPromise) {
            supabaseClientPromise = import('./supabase/client').then(({ createClient }) => createClient());
          }
          const supabase = await supabaseClientPromise;
          const { data: { session }, error } = await supabase.auth.getSession();
          if (error || !session?.access_token) return {};

          const expiresAt = session.expires_at ?? 0;
          const nowSec = Math.floor(Date.now() / 1000);
          if (expiresAt > 0 && expiresAt - nowSec < 60) {
            const { data: refreshed } = await supabase.auth.refreshSession();
            if (refreshed.session?.access_token) {
              return { Authorization: `Bearer ${refreshed.session.access_token}` } as Record<string, string>;
            }
          }

          return { Authorization: `Bearer ${session.access_token}` } as Record<string, string>;
        })(),
        new Promise<Record<string, string>>((resolve) =>
          setTimeout(() => resolve({}), AUTH_HEADER_TIMEOUT_MS),
        ),
      ]);
      const hasAuthHeader = typeof result.Authorization === 'string' && result.Authorization.length > 0;
      authHeadersCache = {
        value: result,
        expiresAt: Date.now() + (hasAuthHeader ? AUTH_HEADER_CACHE_MS : AUTH_HEADER_EMPTY_CACHE_MS),
      };
      return result;
    } catch {
      authHeadersCache = { value: {}, expiresAt: Date.now() + 1000 };
      return {};
    } finally {
      authHeadersInFlight = null;
    }
  })();

  return authHeadersInFlight;
}

interface ApiFetchOptions extends RequestInit {
  businessId?: string;
  authHeadersOverride?: Record<string, string>;
  _retried?: boolean;
  /**
   * Skip the 5s GET cache. For endpoints where a caller needs the live value
   * *now* — the non-GET invalidation below is prefix-based, so a mutation on
   * one path can't evict a differently-prefixed read (e.g. PATCH
   * /admin/feature-flags cannot evict GET /feature-flags).
   */
  skipGetCache?: boolean;
}

const GET_CACHE_TTL_MS = 5000;
const GET_CACHE_MAX_SIZE = 150;
const getCache = new Map<string, { data: any; expiresAt: number }>();
const inflightGets = new Map<string, Promise<any>>();

function pruneGetCache() {
  if (getCache.size <= GET_CACHE_MAX_SIZE) return;
  const now = Date.now();
  for (const [k, v] of getCache) {
    if (v.expiresAt <= now) getCache.delete(k);
  }
  while (getCache.size > GET_CACHE_MAX_SIZE) {
    const first = getCache.keys().next().value;
    if (first) getCache.delete(first); else break;
  }
}

async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const { businessId, authHeadersOverride, _retried, skipGetCache, ...requestOptions } = options;
  const method = (requestOptions.method || 'GET').toUpperCase();

  // Deduplicate + cache GET requests (5s TTL, shares in-flight)
  if (method === 'GET' && !authHeadersOverride && !_retried && !skipGetCache) {
    const biz = businessId || (typeof window !== 'undefined' ? localStorage.getItem('sweetspot_current_business_id') : '') || '';
    // Identity is part of the key: without it a cached GET from the admin's own
    // session would be served while viewing as someone else.
    const cacheKey = `${path}:${biz}:${getImpersonation()?.user.id ?? ''}`;
    const cached = getCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) return cached.data as T;
    const inflight = inflightGets.get(cacheKey);
    if (inflight) return inflight as Promise<T>;
    const promise = (async () => {
      try {
        const result = await apiFetchInner<T>(path, options);
        getCache.set(cacheKey, { data: result, expiresAt: Date.now() + GET_CACHE_TTL_MS });
        pruneGetCache();
        return result;
      } finally {
        inflightGets.delete(cacheKey);
      }
    })();
    inflightGets.set(cacheKey, promise);
    return promise;
  }

  if (method !== 'GET') {
    const prefix = path.split('/').slice(0, 3).join('/');
    for (const [k] of getCache) {
      if (k.startsWith(prefix)) getCache.delete(k);
    }
  }

  return apiFetchInner<T>(path, options);
}

// Use for list endpoints whose contract is "always returns an array".
// Normalizes null/object responses (and same-shape regressions) to [] so
// every consumer can safely call .length / .map / for..of without guarding.
async function apiFetchArray<T = any>(path: string, options?: ApiFetchOptions): Promise<T[]> {
  const data = await apiFetch<unknown>(path, options);
  return Array.isArray(data) ? (data as T[]) : [];
}

async function apiFetchInner<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const { businessId, authHeadersOverride, _retried, skipGetCache: _skipGetCache, ...requestOptions } = options;
  const authHeaders = authHeadersOverride ?? await getAuthHeaders();
  const businessHeaders = businessId
    ? { 'X-Business-Id': businessId }
    : getBusinessIdHeader();
  const res = await fetch(`${API_URL}${path}`, {
    ...requestOptions,
    headers: { 'Content-Type': 'application/json', ...authHeaders, ...businessHeaders, ...getImpersonationHeader(), ...requestOptions.headers },
    cache: 'no-store' as RequestCache,
  });
  if (!res.ok) {
    if (res.status === 401 && !_retried) {
      authHeadersCache = null;
      try {
        if (!supabaseClientPromise) {
          supabaseClientPromise = import('./supabase/client').then(({ createClient }) => createClient());
        }
        const supabase = await supabaseClientPromise;
        const { data: refreshed } = await supabase.auth.refreshSession();
        if (refreshed.session?.access_token) {
          const freshHeaders = { Authorization: `Bearer ${refreshed.session.access_token}` };
          authHeadersCache = { value: freshHeaders, expiresAt: Date.now() + AUTH_HEADER_CACHE_MS };
          return apiFetch<T>(path, { ...options, authHeadersOverride: freshHeaders, _retried: true } as any);
        }
      } catch {
        // refresh failed – fall through to original error
      }
    }
    const body = await res.json().catch(() => ({}));
    const rawMessage = (body as any)?.message;
    const message = Array.isArray(rawMessage)
      ? rawMessage.join(', ')
      : rawMessage || `API error: ${res.status}`;
    const error = new ApiError(message, res.status, body);
    if (res.status === 402) dispatchUpgradeRequired(message);
    throw error;
  }
  return res.json();
}

export interface AuthMeResponse {
  id: string;
  name: string | null;
  email: string;
}

export const authApi = {
  getMe: () => apiFetch<AuthMeResponse>('/auth/me'),
};

// ─── Account deletion (App Store guideline 5.1.1(v)) ───

export interface AccountDeletionPreview {
  email: string;
  businesses: Array<{
    id: string;
    name: string;
    slug: string | null;
    /** Sole-owned: hidden and anonymized. Otherwise handed to a co-owner. */
    willBeRetired: boolean;
  }>;
  activeListings: number;
  memberships: number;
}

export interface AccountDeletionResult {
  success: true;
  alreadyDeleted: boolean;
  authIdentityDeleted: boolean;
  businessesRetired: number;
  businessesTransferred: number;
}

export const accountApi = {
  getDeletionPreview: () =>
    apiFetch<AccountDeletionPreview>('/account/deletion-preview', { skipGetCache: true }),
  deleteAccount: (reason?: string) =>
    apiFetch<AccountDeletionResult>('/account/delete', {
      method: 'POST',
      body: JSON.stringify({ confirm: 'DELETE', reason }),
    }),
};

// ─── Push devices ───

export interface RegisteredDevice {
  id: string;
  platform: string;
  appVersion: string | null;
  lastSeenAt: string;
}

export interface RegisterDeviceInput {
  token: string;
  platform: 'ios' | 'android';
  appVersion?: string;
  build?: string;
  locale?: string;
}

export interface SiteDomainProvisioning {
  provider: 'vercel';
  configured: boolean;
  projectRegistered: boolean;
  projectVerified: boolean;
  routingReady: boolean;
  tlsAutomatic: boolean;
  configuredBy: string | null;
  verification: Array<{ type: string; domain: string; value: string; reason?: string }>;
  dns: Array<{ type: 'A' | 'CNAME'; name: string; value: string }>;
}

/**
 * The push device registry. `register` is a PUT because it repeats on every
 * cold start — APNs rotates tokens — and the server upserts by token, so a
 * phone that changes accounts moves rather than duplicates.
 */
export const devicesApi = {
  register: (input: RegisterDeviceInput) =>
    apiFetch<{ ok: true }>('/account/devices', {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
  unregister: (token: string) =>
    apiFetch<{ ok: true }>('/account/devices', {
      method: 'DELETE',
      body: JSON.stringify({ token }),
    }),
  list: () => apiFetch<{ devices: RegisteredDevice[] }>('/account/devices', { skipGetCache: true }),
};

export const businessesApi = {
  list: () => apiFetch<any[]>('/businesses'),
  getCurrent: () => apiFetch<any>('/businesses/current'),
  ensureSlug: () =>
    apiFetch<{ id: string; slug: string }>('/businesses/current/ensure-slug', { method: 'POST' }),
  get: (id: string, options?: ApiFetchOptions) => apiFetch<any>(`/businesses/${id}`, options),
  create: (data: any) => apiFetch<any>('/businesses', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: any, options?: ApiFetchOptions) => apiFetch<any>(`/businesses/${id}`, { ...options, method: 'PATCH', body: JSON.stringify(data) }),
  /** Checks Sweetspot ownership, registers the domain with Vercel and reports DNS setup. */
  verifySiteDomain: (id: string) =>
    apiFetch<{
      verified: boolean;
      ownershipVerified?: boolean;
      pending?: string | null;
      siteDomain?: string | null;
      siteDomainVerifiedAt?: string | null;
      provisioning?: SiteDomainProvisioning;
    }>(
      `/businesses/${id}/site-domain/verify`,
      { method: 'POST' },
    ),
  getSiteDomainStatus: (id: string) =>
    apiFetch<{
      siteDomain: string | null;
      pending: string | null;
      siteDomainVerifiedAt?: string | null;
      provisioning: SiteDomainProvisioning | null;
    }>(`/businesses/${id}/site-domain/status`, { skipGetCache: true }),
  removeSiteDomain: (id: string) =>
    apiFetch<any>(`/businesses/${id}/site-domain`, { method: 'DELETE' }),
  aiComplete: (query: string, website?: string) =>
    apiFetch<{
      name: string;
      website: string;
      addressStreet: string;
      locationCity: string;
      locationRegion: string;
      locationCountry: string;
      category: string;
    } | null>('/businesses/ai-complete', {
      method: 'POST',
      body: JSON.stringify({ query, website }),
    }),
  getOnboardingStatus: () =>
    apiFetch<{
      profileComplete: boolean;
      businessReady: boolean;
      bankConnected: boolean;
      storefrontReady: boolean;
      hasProducts: boolean;
      menuItemCount: number;
      hasRecipes: boolean;
      hasIngredients: boolean;
      slug: string | null;
      siteSlug: string | null;
      sitePublished: boolean;
      nativeOrderingAvailable: boolean;
      orderButtonReady: boolean;
      hoursReady: boolean;
      pageReady: boolean;
      siteUrl: string | null;
      businessName: string;
    }>('/businesses/onboarding-status'),
  findClaimable: (query: string) =>
    apiFetch<Array<{
      id: string;
      name: string;
      slug: string | null;
      category: string;
      locationNeighborhood: string | null;
      locationCity: string | null;
      locationRegion: string | null;
      coverImageUrl: string | null;
      avatarUrl: string | null;
      handleInstagram: string | null;
      _count: { marketplaceProducts: number };
    }>>(`/businesses/claimable?q=${encodeURIComponent(query)}`),
  claim: (businessId: string) =>
    apiFetch<any>(`/businesses/claim/${businessId}`, { method: 'POST' }),
  uploadSitePhoto: async (businessId: string, file: File): Promise<{ imageUrl: string; assetRef: string }> => {
    const form = new FormData(); form.append('file', file);
    const res = await fetch(`${API_URL}/businesses/${businessId}/upload-site-photo`, {
      method: 'POST', headers: { ...await getAuthHeaders(), ...getImpersonationHeader() }, body: form,
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Photo upload failed');
    return res.json();
  },
  uploadCover: async (businessId: string, file: File): Promise<{ coverImageUrl: string }> => {
    const authHeaders = await getAuthHeaders();
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API_URL}/businesses/${businessId}/upload-cover`, {
      method: 'POST',
      headers: { ...authHeaders, ...getImpersonationHeader() },
      body: formData,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || 'Upload failed');
    }
    return res.json();
  },
  uploadAvatar: async (businessId: string, file: File): Promise<{ avatarUrl: string }> => {
    const authHeaders = await getAuthHeaders();
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API_URL}/businesses/${businessId}/upload-avatar`, {
      method: 'POST',
      headers: { ...authHeaders, ...getImpersonationHeader() },
      body: formData,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || 'Upload failed');
    }
    return res.json();
  },
  /** Brand assets for the storefront: 'logo' = wide wordmark, 'logo-mark' = square mark. PNG/JPEG/WebP, transparent PNG recommended. */
  uploadBrandImage: async (
    businessId: string,
    kind: 'logo' | 'logo-mark',
    file: File,
  ): Promise<{ logoUrl: string | null; logoMarkUrl: string | null }> => {
    const authHeaders = await getAuthHeaders();
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API_URL}/businesses/${businessId}/upload-${kind}`, {
      method: 'POST',
      headers: { ...authHeaders, ...getImpersonationHeader() },
      body: formData,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || 'Upload failed');
    }
    return res.json();
  },
  removeBrandImage: (businessId: string, kind: 'logo' | 'logo-mark') =>
    apiFetch<{ logoUrl: string | null; logoMarkUrl: string | null }>(`/businesses/${businessId}/${kind}`, { method: 'DELETE' }),
};

export const ingredientsApi = {
  list: () => apiFetch<any[]>('/ingredients'),
  get: (id: string) => apiFetch<any>(`/ingredients/${id}`),
  create: (data: any) => apiFetch<any>('/ingredients', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: any) => apiFetch<any>(`/ingredients/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) => apiFetch<any>(`/ingredients/${id}`, { method: 'DELETE' }),
};

export const recipesApi = {
  list: () => apiFetch<any[]>('/recipes'),
  get: (id: string) => apiFetch<any>(`/recipes/${id}`),
  create: (data: any) => apiFetch<any>('/recipes', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: any) => apiFetch<any>(`/recipes/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) => apiFetch<any>(`/recipes/${id}`, { method: 'DELETE' }),
  metrics: (id: string) => apiFetch<any>(`/recipes/${id}/metrics`),
  addIngredient: (id: string, data: any) => apiFetch<any>(`/recipes/${id}/ingredients`, { method: 'POST', body: JSON.stringify(data) }),
  removeIngredient: (recipeId: string, riId: string) => apiFetch<any>(`/recipes/${recipeId}/ingredients/${riId}`, { method: 'DELETE' }),
  linkToCatalog: (id: string) => apiFetch<any>(`/recipes/${id}/link-catalog`, { method: 'POST' }),
};

export const salesApi = {
  upsert: (data: any) => apiFetch<any>('/sales', { method: 'POST', body: JSON.stringify(data) }),
  byMonth: (month: string) => apiFetch<any[]>(`/sales?month=${month}`),
  byRecipe: (recipeId: string) => apiFetch<any[]>(`/sales/recipe/${recipeId}`),
};

export interface FeatureFlagRow {
  key: string;
  label: string;
  description: string;
  group: string;
  defaultEnabled: boolean;
  enabled: boolean;
  /** True when an admin override is stored; false means the code-side default is in effect. */
  overridden: boolean;
  updatedBy: string | null;
  updatedAt: string | null;
}

export const featureFlagsApi = {
  // skipGetCache: feature-flags.tsx keeps its own 60s cache and forces a re-read
  // right after an admin toggle. Without this the 5s GET cache hands back the
  // pre-toggle map and it gets re-cached for a full TTL — the killed flag
  // reappears. A PATCH to /admin/feature-flags cannot evict this key.
  getFlags: () => apiFetch<{ flags: Record<string, boolean> }>('/feature-flags', { skipGetCache: true }),
  adminList: () => apiFetch<FeatureFlagRow[]>('/admin/feature-flags'),
  adminSet: (key: string, enabled: boolean) =>
    apiFetch<FeatureFlagRow>(`/admin/feature-flags/${encodeURIComponent(key)}`, {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    }),
  adminReset: (key: string) =>
    apiFetch<FeatureFlagRow>(`/admin/feature-flags/${encodeURIComponent(key)}`, {
      method: 'DELETE',
    }),
};

export const dashboardApi = {
  productCards: (month?: string, businessId?: string) =>
    apiFetch<any[]>(`/dashboard/products${month ? `?month=${month}` : ''}`, { businessId }),
  recipeMonthly: (recipeId: string, month: string) => apiFetch<any>(`/dashboard/products/${recipeId}/monthly?month=${month}`),
  pricingSuggestion: (recipeId: string, targetMargin?: number) => apiFetch<any>(`/dashboard/products/${recipeId}/pricing${targetMargin ? `?targetMargin=${targetMargin}` : ''}`),
};

// ─── Report & block (App Store guideline 1.2) ───

export type ReportTargetType =
  | 'BUSINESS'
  | 'LISTING'
  | 'MESSAGE'
  | 'CONVERSATION'
  | 'RATING'
  | 'USER';

export type ReportReason =
  | 'SPAM'
  | 'SCAM_OR_FRAUD'
  | 'HARASSMENT'
  | 'INAPPROPRIATE'
  | 'COUNTERFEIT'
  | 'OTHER';

export interface BlockedBusiness {
  id: string;
  name: string;
  slug: string | null;
  avatarUrl: string | null;
  locationCity: string | null;
  blockedAt: string;
  reason: string | null;
}

export const marketplaceApi = {
  getSlots: (params?: { from?: string; to?: string; type?: 'pickup_availability' | 'pickup_need' | 'delivery_availability' }) => {
    const qs = new URLSearchParams();
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    if (params?.type) qs.set('type', params.type);
    const query = qs.toString();
    return apiFetch<any[]>(`/marketplace/slots${query ? `?${query}` : ''}`);
  },
  createSlot: (data: { startAt: string; endAt: string; slotType?: 'pickup_availability' | 'pickup_need' | 'delivery_availability'; contextType?: string; contextId?: string }) =>
    apiFetch<any>('/marketplace/slots', { method: 'POST', body: JSON.stringify(data) }),
  createOffer: (data: { productId: string; quantity: number; price?: number; slotIds?: string[]; preferredPickupTime?: string }) =>
    apiFetch<any>('/marketplace/offers', { method: 'POST', body: JSON.stringify(data) }),
  createCheckoutOffers: (data: {
    items: Array<{ productId: string; quantity: number; price?: number; selectedOptionIds?: string[] }>;
    vendorNotes?: Array<{ vendorSlug: string; note: string }>;
    idempotencyKey?: string;
    fulfillment?: { type: string; when?: string };
  }) =>
    apiFetch<{
      checkoutGroupId: string;
      offerCount: number;
      offerIds: string[];
    }>('/marketplace/offers/checkout', { method: 'POST', body: JSON.stringify(data) }),
  getOffers: (as: 'buyer' | 'seller', status?: string, businessId?: string, since?: Date) =>
    apiFetchArray<any>(
      `/marketplace/offers?as=${as}${status ? `&status=${status}` : ''}${since ? `&since=${since.toISOString()}` : ''}`,
      { businessId },
    ),
  getNavCounts: () =>
    apiFetch<{ actionable: number; received: number; sent: number }>(
      '/marketplace/offers/nav-counts',
    ),
  getOffer: (id: string) => apiFetch<any>(`/marketplace/offers/${id}`),
  acceptOffer: (id: string, slotId?: string, preferredPickupTime?: string, businessId?: string, deliveryAddress?: string, deliveryNotes?: string, price?: number, quantity?: number) =>
    apiFetch<any>(`/marketplace/offers/${id}/accept`, { method: 'PATCH', body: JSON.stringify({ slotId, preferredPickupTime, deliveryAddress, deliveryNotes, price, quantity }), businessId }),
  acceptOfferBatch: (offerIds: string[], businessId?: string, deliveryAddress?: string, deliveryNotes?: string) =>
    apiFetch<{ accepted: number; offerIds: string[]; failed: Array<{ offerId: string; reason: string }> }>('/marketplace/offers/accept-batch', { method: 'PATCH', body: JSON.stringify({ offerIds, deliveryAddress, deliveryNotes }), businessId }),
  rejectOffer: (id: string, businessId?: string) =>
    apiFetch<any>(`/marketplace/offers/${id}/reject`, { method: 'PATCH', businessId }),
  cancelOrder: (id: string, businessId?: string) =>
    apiFetch<{ status: string; offerIds: string[]; paymentStatus: string; paymentOutcome: 'refunded' | 'released' | 'none'; refundFeeLossCents: number | null }>(
      `/marketplace/offers/${id}/cancel`,
      { method: 'PATCH', businessId },
    ),
  counterOffer: (id: string, data: { price?: number; quantity?: number }, businessId?: string) =>
    apiFetch<any>(`/marketplace/offers/${id}/counter`, { method: 'PATCH', body: JSON.stringify(data), businessId }),
  amendOffer: (id: string, data: { price?: number; quantity?: number }, businessId?: string) =>
    apiFetch<any>(`/marketplace/offers/${id}/amend`, { method: 'PATCH', body: JSON.stringify(data), businessId }),
  completeOffer: (id: string, businessId?: string) =>
    apiFetch<any>(`/marketplace/offers/${id}/complete`, { method: 'PATCH', businessId }),
  createBuyPost: (data: { title: string; details?: string; category?: string; desiredQty?: number; budgetMax?: number; cityKey: string }) =>
    apiFetch<any>('/marketplace/buy-posts', { method: 'POST', body: JSON.stringify(data) }),
  getBuyPosts: (params?: { status?: string; feed?: boolean; city?: string; excludeOwn?: boolean }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.feed) qs.set('feed', 'true');
    if (params?.city) qs.set('city', params.city);
    if (params?.excludeOwn === false) qs.set('excludeOwn', 'false');
    const query = qs.toString();
    return apiFetchArray<any>(`/marketplace/buy-posts${query ? `?${query}` : ''}`);
  },
  createOfferForBuyPost: (data: { buyPostId: string; quantity: number; price: number; unit: string; slotIds?: string[] }) =>
    apiFetch<any>('/marketplace/offers/for-buy-post', { method: 'POST', body: JSON.stringify(data) }),
  createContactOffer: (sellerBusinessId: string) =>
    apiFetch<{ conversationId: string; otherBusinessId: string }>('/marketplace/offers/contact', { method: 'POST', body: JSON.stringify({ sellerBusinessId }) }),
  getSellerBuyerDiscounts: (sellerBusinessId: string) =>
    apiFetch<any[]>(`/marketplace/discounts/${sellerBusinessId}`),
  getProductDiscount: (sellerBusinessId: string, productId: string) =>
    apiFetch<any | null>(`/marketplace/discounts/${sellerBusinessId}/${productId}`),
  getConversations: (businessId?: string, signal?: AbortSignal) => apiFetchArray<any>('/marketplace/conversations', { businessId, signal, skipGetCache: !!signal }),
  getConversationWithUser: (otherBusinessId: string, businessId?: string) =>
    apiFetch<any>(`/marketplace/conversations/with/${otherBusinessId}`, { businessId }),
  getUnreadMessageCount: async () => {
    const authHeaders = await getAuthHeaders();
    const businessIdHeader = getBusinessIdHeader()['X-Business-Id'] || '';
    const authHeader = authHeaders.Authorization || '';
    const cacheKey = `${businessIdHeader}|${authHeader}`;

    if (unreadCountCache && unreadCountCache.key === cacheKey && unreadCountCache.expiresAt > Date.now()) {
      return Promise.resolve(unreadCountCache.value);
    }
    if (unreadCountInFlight && unreadCountInFlight.key === cacheKey) return unreadCountInFlight.promise;
    const promise = apiFetch<{ count: number }>('/marketplace/conversations/unread-count', {
      businessId: businessIdHeader || undefined,
      authHeadersOverride: authHeaders,
    })
      .then((value) => {
        unreadCountCache = { key: cacheKey, value, expiresAt: Date.now() + UNREAD_COUNT_CACHE_MS };
        return value;
      })
      .finally(() => {
        if (unreadCountInFlight?.key === cacheKey) unreadCountInFlight = null;
      });
    unreadCountInFlight = { key: cacheKey, promise };
    return promise;
  },
  getOfferConversation: (offerId: string, businessId?: string) =>
    apiFetch<any>(`/marketplace/offers/${offerId}/conversation`, { businessId }),
  getConversationMessages: (conversationId: string, businessId?: string) =>
    apiFetch<any[]>(`/marketplace/conversations/${conversationId}/messages`, { businessId }),
  sendMessage: (conversationId: string, body: string, businessId?: string) =>
    apiFetch<any>(`/marketplace/conversations/${conversationId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ body }),
      businessId,
    }),
  updateBuyPost: (id: string, data: Partial<{ title: string; details: string; category: string; budgetMax: number; status: string }>) =>
    apiFetch<any>(`/marketplace/buy-posts/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteBuyPost: (id: string) =>
    apiFetch<{ success: boolean }>(`/marketplace/buy-posts/${id}`, { method: 'DELETE' }),
  contactBuyPostOwner: (buyPostId: string, message: string) =>
    apiFetch<{ success: boolean }>(`/marketplace/buy-posts/${buyPostId}/contact`, { method: 'POST', body: JSON.stringify({ message }) }),
  createSlotRule: (data: {
    slotType: string;
    rrule: string;
    startTime: string;
    endTime: string;
    effectiveFrom: string;
    effectiveTo?: string;
    timezone?: string;
    contextType?: string;
    contextId?: string;
  }) =>
    apiFetch<any>('/marketplace/slot-rules', { method: 'POST', body: JSON.stringify(data) }),
  blockBusiness: (blockedId: string, reason?: string) =>
    apiFetch<any>(`/marketplace/block/${blockedId}`, {
      method: 'POST',
      body: JSON.stringify(reason ? { reason } : {}),
    }),
  unblockBusiness: (blockedId: string) =>
    apiFetch<{ success: boolean }>(`/marketplace/block/${blockedId}`, { method: 'DELETE' }),
  getBlockedBusinesses: () => apiFetchArray<BlockedBusiness>('/marketplace/blocks'),
  getBlockStatus: (otherBusinessId: string) =>
    apiFetch<{ blocked: boolean }>(`/marketplace/blocks/${otherBusinessId}`),
  /** Report user-generated content (App Store guideline 1.2). */
  report: (input: {
    targetType: ReportTargetType;
    targetId: string;
    reason: ReportReason;
    details?: string;
  }) =>
    apiFetch<{ id: string; status: string; createdAt: string; duplicate: boolean }>(
      '/marketplace/report',
      { method: 'POST', body: JSON.stringify(input) },
    ),
  getProducts: (active?: boolean, productType?: 'goods' | 'service', businessId?: string) => {
    const qs = new URLSearchParams();
    if (active === false) qs.set('active', 'false');
    if (productType) qs.set('type', productType);
    const query = qs.toString();
    return apiFetch<any[]>(`/marketplace/products${query ? `?${query}` : ''}`, { businessId });
  },
  getProduct: (id: string, businessId?: string, signal?: AbortSignal) =>
    apiFetch<any>(`/marketplace/products/${id}`, { businessId, signal, skipGetCache: true }),
  reorderProducts: (productIds: string[], businessId?: string) =>
    apiFetch<{ success: boolean }>('/marketplace/products/reorder', {
      method: 'POST',
      body: JSON.stringify({ productIds }),
      businessId,
    }),
  createProduct: (data: {
    productType?: 'goods' | 'service';
    title: string;
    description?: string;
    name2?: string | null;
    description2?: string | null;
    ingredients?: string[];
    allergies?: string[];
    unit?: string;
    price: number;
    minQty?: number;
    leadTimeDays?: number;
    category?: string;
    cityKey?: string;
    imageUrl?: string;
    isActive?: boolean;
  }, businessId?: string) =>
    apiFetch<any>('/marketplace/products', {
      method: 'POST',
      body: JSON.stringify(data),
      businessId,
    }),
  updateProduct: (id: string, data: Partial<{
    productType: 'goods' | 'service';
    title: string;
    description: string;
    name2: string | null;
    description2: string | null;
    ingredients: string[];
    allergies: string[];
    unit: string;
    price: number;
    minQty: number;
    leadTimeDays: number;
    category: string;
    cityKey: string;
    imageUrl: string;
    isActive: boolean;
    showOnSite: boolean;
    showInMarketplace: boolean;
  }>) => apiFetch<any>(`/marketplace/products/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteProduct: (id: string) => apiFetch<{ success: boolean }>(`/marketplace/products/${id}`, { method: 'DELETE' }),
  createRecipeFromProduct: (id: string) => apiFetch<any>(`/marketplace/products/${id}/create-recipe`, { method: 'POST' }),
  generateImage: (id: string, extraPrompt?: string) =>
    apiFetch<{ imageUrl: string }>(`/marketplace/products/${id}/generate-image`, {
      method: 'POST',
      body: JSON.stringify({ extraPrompt }),
    }),
  previewImage: (id: string, extraPrompt?: string, businessId?: string, signal?: AbortSignal) =>
    apiFetch<{ imageDataUrl: string; referenceUsed: boolean }>(`/marketplace/products/${id}/preview-image`, {
      method: 'POST', body: JSON.stringify({ extraPrompt }), businessId, signal,
    }),
  uploadImage: async (
    id: string,
    file: File,
    businessId?: string,
  ): Promise<{
    imageUrl: string;
    imageVariants?: Record<string, { url: string; width: number; height: number }>;
  }> => {
    const authHeaders = await getAuthHeaders();
    const businessHeaders = businessId
      ? { 'X-Business-Id': businessId }
      : getBusinessIdHeader();
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API_URL}/marketplace/products/${id}/upload-image`, {
      method: 'POST',
      headers: { ...authHeaders, ...businessHeaders, ...getImpersonationHeader() },
      body: formData,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || `Upload failed: ${res.status}`);
    }
    return res.json();
  },
  /** Seller view of a product's option groups (size, milk, …); deltas in whole cents. */
  getProductOptionGroups: (id: string, businessId?: string) =>
    apiFetchArray<ProductOptionGroupInput>(`/marketplace/products/${id}/option-groups`, { businessId }),
  /** Replace-all: `groups` is the product's complete option set; [] clears it. */
  replaceProductOptionGroups: (id: string, groups: ProductOptionGroupInput[], businessId?: string) =>
    apiFetch<ProductOptionGroupInput[]>(`/marketplace/products/${id}/option-groups`, {
      method: 'PUT',
      body: JSON.stringify(groups),
      businessId,
    }),
};

/** One choice inside a product option group. `priceDeltaCents` is whole cents, ±10000. */
export interface ProductOptionInput {
  id?: string;
  name: string;
  priceDeltaCents: number;
  isSoldOut: boolean;
  sortOrder: number;
}

/** Product option group as the seller edits it — the PUT body and the GET/PUT response. */
export interface ProductOptionGroupInput {
  id?: string;
  name: string;
  required: boolean;
  minSelect: number;
  maxSelect: number;
  sortOrder: number;
  options: ProductOptionInput[];
}

export interface MarketplaceFeedItem {
  id: string;
  type: 'product' | 'buypost';
  title: string;
  description?: string | null;
  price?: number;
  unit?: string;
  productType?: string;
  category?: string | null;
  imageUrl?: string | null;
  cityKey: string;
  createdAt: string;
  business: {
    id: string;
    name: string;
    slug?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    avatarUrl?: string | null;
    category?: string | null;
  };
}

export interface PublicInstagramProductPhoto {
  id: string;
  previewUrl: string;
  permalink: string;
  username: string;
}

export interface StoreFeedItem {
  id: string;
  name: string;
  slug: string | null;
  avatarUrl: string | null;
  coverImageUrl: string | null;
  bio: string | null;
  category: string | null;
  locationNeighborhood: string | null;
  locationCity: string | null;
  claimStatus: string | null;
  productCount: number;
  previewProducts: { imageUrl: string | null; title: string; instagramPhoto?: PublicInstagramProductPhoto | null }[];
  avgRating: number | null;
  ratingCount: number;
}

export interface ProductFeedItem {
  id: string;
  title: string;
  description: string | null;
  price: string | number;
  unit: string;
  minQty: number;
  leadTimeDays: number;
  category: string | null;
  productType: string;
  imageUrl: string | null;
  instagramPhoto?: PublicInstagramProductPhoto | null;
  createdAt: string;
  sellerBusiness: {
    id: string;
    name: string;
    slug: string | null;
    avatarUrl: string | null;
    category: string | null;
  };
}

export const storefrontApi = {
  getStoresFeed: (cursor?: string, limit?: number) => {
    const qs = new URLSearchParams();
    if (cursor) qs.set('cursor', cursor);
    if (limit) qs.set('limit', limit.toString());
    const query = qs.toString();
    return apiFetch<{ items: StoreFeedItem[]; nextCursor?: string }>(`/storefront/feed/stores${query ? `?${query}` : ''}`, { skipGetCache: true });
  },
  /** `city: ''` means "every city" — the API skips the city filter when it can't resolve one. */
  getProductsFeed: (city: string, productType?: 'goods' | 'service', limit?: number) => {
    const qs = new URLSearchParams({ city });
    if (productType) qs.set('type', productType);
    if (limit) qs.set('limit', String(limit));
    return apiFetch<ProductFeedItem[]>(`/storefront/feed/products?${qs.toString()}`, { skipGetCache: true });
  },
  getBuyPostsFeed: (city: string) => apiFetch<any[]>(`/storefront/feed/buy-posts?city=${encodeURIComponent(city)}`),
  getMarketplaceFeed: (
    city: string,
    productType?: 'goods' | 'service',
    bounds?: { neLat: number; neLng: number; swLat: number; swLng: number },
  ) => {
    const qs = new URLSearchParams({ city });
    if (productType) qs.set('type', productType);
    if (bounds) {
      qs.set('neLat', String(bounds.neLat));
      qs.set('neLng', String(bounds.neLng));
      qs.set('swLat', String(bounds.swLat));
      qs.set('swLng', String(bounds.swLng));
    }
    return apiFetch<{ products: any[]; buyPosts: any[] }>(`/storefront/feed/marketplace?${qs.toString()}`, { skipGetCache: true });
  },
  getBusiness: (slug: string) => apiFetch<any>(`/storefront/business/${slug}`),
  getProducts: (slug: string) => apiFetch<any[]>(`/storefront/business/${slug}/products`, { skipGetCache: true }),
  getProduct: (slug: string, productId: string) =>
    apiFetch<{ product: any; business: any }>(`/storefront/business/${slug}/products/${productId}`, { skipGetCache: true }),
  getSlots: (slug: string, params?: { from?: string; to?: string }) => {
    const qs = new URLSearchParams();
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    const query = qs.toString();
    return apiFetch<any[]>(`/storefront/business/${slug}/slots${query ? `?${query}` : ''}`);
  },
};

export interface GamificationStatus {
  level: { level: number; name: string; title: string; requiredMilestones: string[] };
  nextLevel: { level: number; name: string; title: string; requiredMilestones: string[] } | null;
  levelProgress: number;
  milestones: Array<{
    key: string;
    label: string;
    description: string;
    category: string;
    icon: string;
    unlocked: boolean;
    unlockedAt: string | null;
  }>;
  streak: { current: number; longest: number; lastActiveWeek: string | null; freezesRemaining: number };
  newlyUnlocked: string[];
}

export const gamificationApi = {
  getStatus: () => apiFetch<GamificationStatus>('/gamification/status'),
  check: () => apiFetch<GamificationStatus>('/gamification/check', { method: 'POST' }),
  recordActivity: () =>
    apiFetch<{
      streak: { current: number; longest: number; lastActiveWeek: string; freezesRemaining: number };
      weekChanged: boolean;
    }>('/gamification/activity', { method: 'POST' }),
};

// ─── Admin ───

export interface AdminKpiSummary {
  firstResponseMedianHours: number | null;
  firstResponseP90Hours: number | null;
  matchRatePercent: number | null;
  fillRatePercent: number | null;
  repeatBuyerPercent: number | null;
  supplierResponseRatePercent: number | null;
  cancelRatePercent: number | null;
  totalBuyPosts: number;
  totalOffers: number;
  totalConfirmed: number;
}

export interface AdminKpiTrendPoint {
  date: string;
  buyPosts: number;
  offers: number;
  matched: number;
  confirmed: number;
  cancelled: number;
  matchRatePercent: number | null;
  fillRatePercent: number | null;
}

export interface AdminOverview {
  totalUsers: number;
  totalBusinesses: number;
  activeBuyPosts: number;
  activeProducts: number;
  totalOffers: number;
  confirmedOffers: number;
}

export interface AdminUserRow {
  id: string;
  email: string;
  name: string | null;
  locationCity: string | null;
  isAdmin: boolean;
  createdAt: string;
  businessCount: number;
  membershipCount: number;
}

export interface AdminRatingRow {
  id: string;
  offerId: string | null;
  role: string;
  score: number;
  comment: string | null;
  flagged: boolean;
  createdAt: string;
  rater: { id: string; name: string | null };
  ratee: { id: string; name: string | null };
  productLinks: Array<{ product: { id: string; title: string } }>;
}

export interface AdminListingRow {
  id: string;
  listingType: 'product' | 'buypost';
  title: string;
  category: string | null;
  cityKey: string;
  createdAt: string;
  business: { id: string; name: string; slug: string | null };
  offerCount: number;
  // product fields
  productType?: string;
  price?: number;
  unit?: string;
  isActive?: boolean;
  imageUrl?: string | null;
  // buypost fields
  details?: string | null;
  desiredQty?: number | null;
  budgetMax?: number | null;
  status?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export interface AdminBusinessRow {
  id: string;
  name: string;
  slug: string | null;
  category: string | null;
  locationNeighborhood: string | null;
  locationCity: string | null;
  claimStatus: string | null;
  isDiscoverable: boolean;
  /** Ordering-site handle and live switch — independent of the marketplace listing. */
  siteSlug: string | null;
  /** The tenant's own domain (bare host), when connected. */
  siteDomain: string | null;
  sitePublished: boolean;
  plan: 'free' | 'plus' | 'pro';
  foundingUntil: string | null;
  foundingSeq: number | null;
  platformFeeBps: number | null;
  createdAt: string;
  ownerEmail: string | null;
  ownerName: string | null;
  productCount: number;
  memberCount: number;
}

export interface AdminReportRow {
  id: string;
  reporterUserId: string;
  targetType: ReportTargetType;
  targetId: string;
  reason: ReportReason;
  details: string | null;
  status: 'OPEN' | 'REVIEWED' | 'DISMISSED';
  createdAt: string;
  reviewedAt: string | null;
  reviewedBy: string | null;
  reporter: { id: string; name: string | null; email: string } | null;
  target: { label: string; href: string | null } | null;
}

export const adminApi = {
  me: () => apiFetch<{ isAdmin: boolean }>('/admin/me'),
  getKpis: (from?: string, to?: string) => {
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    const query = qs.toString();
    return apiFetch<AdminKpiSummary>(`/admin/kpis${query ? `?${query}` : ''}`);
  },
  getKpiTrends: (from?: string, to?: string, granularity?: 'day' | 'week') => {
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    if (granularity) qs.set('granularity', granularity);
    const query = qs.toString();
    return apiFetch<AdminKpiTrendPoint[]>(`/admin/kpis/trends${query ? `?${query}` : ''}`);
  },
  getOverview: () => apiFetch<AdminOverview>('/admin/overview'),
  getUsers: (params?: { page?: number; limit?: number; search?: string }) => {
    const qs = new URLSearchParams();
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.search) qs.set('search', params.search);
    const query = qs.toString();
    return apiFetch<PaginatedResponse<AdminUserRow>>(`/admin/users${query ? `?${query}` : ''}`);
  },
  getListings: (params?: { page?: number; limit?: number; search?: string; type?: 'product' | 'buypost' }) => {
    const qs = new URLSearchParams();
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.search) qs.set('search', params.search);
    if (params?.type) qs.set('type', params.type);
    const query = qs.toString();
    return apiFetch<PaginatedResponse<AdminListingRow>>(`/admin/listings${query ? `?${query}` : ''}`);
  },
  impersonate: (userId: string) =>
    apiFetch<{ token: string; expiresInSeconds: number; mode: ImpersonationMode; user: { id: string; email: string | null; name: string | null } }>(
      `/admin/impersonate/${userId}`,
      { method: 'POST' },
    ),
  updateUser: (id: string, data: { email?: string; name?: string; isAdmin?: boolean }) =>
    apiFetch<any>(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteUser: (id: string) =>
    apiFetch<{ success: boolean }>(`/admin/users/${id}`, { method: 'DELETE' }),
  getBusinesses: (params?: { page?: number; limit?: number; search?: string }) => {
    const qs = new URLSearchParams();
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.search) qs.set('search', params.search);
    const query = qs.toString();
    return apiFetch<PaginatedResponse<AdminBusinessRow>>(`/admin/businesses${query ? `?${query}` : ''}`);
  },
  updateBusiness: (
    id: string,
    data: {
      name?: string;
      slug?: string;
      category?: string;
      claimStatus?: string;
      siteSlug?: string | null;
      siteDomain?: string | null;
      sitePublished?: boolean;
      plan?: 'free' | 'plus' | 'pro';
      foundingUntil?: string | null;
      foundingSeq?: number | null;
      platformFeeBps?: number | null;
    },
  ) =>
    apiFetch<any>(`/admin/businesses/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteBusiness: (id: string) =>
    apiFetch<{ success: boolean }>(`/admin/businesses/${id}`, { method: 'DELETE' }),
  updateProduct: (id: string, data: { title?: string; price?: number; isActive?: boolean }) =>
    apiFetch<any>(`/admin/products/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteProduct: (id: string) =>
    apiFetch<{ success: boolean }>(`/admin/products/${id}`, { method: 'DELETE' }),
  getRatings: (params?: { page?: number; limit?: number; search?: string; flagged?: boolean }) => {
    const qs = new URLSearchParams();
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.search) qs.set('search', params.search);
    if (params?.flagged) qs.set('flagged', 'true');
    const query = qs.toString();
    return apiFetch<PaginatedResponse<AdminRatingRow>>(`/admin/ratings${query ? `?${query}` : ''}`);
  },
  flagRating: (id: string, flagged: boolean) =>
    apiFetch<any>(`/admin/ratings/${id}/flag`, { method: 'PATCH', body: JSON.stringify({ flagged }) }),
  deleteRating: (id: string) =>
    apiFetch<{ success: boolean }>(`/admin/ratings/${id}`, { method: 'DELETE' }),
  getReports: (params?: {
    page?: number;
    limit?: number;
    status?: AdminReportRow['status'];
    targetType?: ReportTargetType;
  }) => {
    const qs = new URLSearchParams();
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.status) qs.set('status', params.status);
    if (params?.targetType) qs.set('targetType', params.targetType);
    const query = qs.toString();
    return apiFetch<PaginatedResponse<AdminReportRow>>(`/admin/reports${query ? `?${query}` : ''}`);
  },
  updateReport: (id: string, status: AdminReportRow['status']) =>
    apiFetch<AdminReportRow>(`/admin/reports/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),
  getEmailTemplates: () =>
    apiFetch<Array<{ key: string; name: string; category: string; subject: string }>>('/admin/email-templates'),
  getEmailTemplate: (key: string) =>
    apiFetch<{ key: string; name: string; category: string; subject: string; html: string }>(`/admin/email-templates/${key}`),
};

// ─── Notifications ───

export interface NotificationPreference {
  eventType: string;
  category: string;
  channel: string;
  enabled: boolean;
}

export interface NotificationLogEntry {
  id: string;
  channel: string;
  eventType: string;
  subject: string | null;
  recipient: string;
  status: string;
  metadata: Record<string, any> | null;
  createdAt: string;
}

export type NotificationChannel = 'email' | 'push';

export interface NotificationEventSetting {
  eventType: string;
  category: string;
  email: boolean;
  push: boolean;
  /** True when a delivered push suppresses the email for this event. */
  realtime: boolean;
}

export interface NotificationSettings {
  events: NotificationEventSetting[];
  devices: RegisteredDevice[];
  /** False when the server has no APNs key — push toggles are informational. */
  pushConfigured: boolean;
}

export const notificationsApi = {
  /** Both channels per event plus the registered devices, in one round trip. */
  getSettings: () =>
    apiFetch<NotificationSettings>('/notifications/settings', { skipGetCache: true }),
  updateSettings: (
    updates: { eventType: string; channel: NotificationChannel; enabled: boolean }[],
  ) =>
    apiFetch<NotificationSettings>('/notifications/settings', {
      method: 'PATCH',
      body: JSON.stringify({ updates }),
    }),
  getPreferences: () => apiFetch<NotificationPreference[]>('/notifications/preferences'),
  updatePreferences: (preferences: { eventType: string; enabled: boolean }[]) =>
    apiFetch<NotificationPreference[]>('/notifications/preferences', {
      method: 'PATCH',
      body: JSON.stringify({ preferences }),
    }),
  getLogs: (params?: { limit?: number; offset?: number }) => {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.offset) qs.set('offset', String(params.offset));
    const query = qs.toString();
    return apiFetch<{ data: NotificationLogEntry[]; meta: { total: number; limit: number; offset: number } }>(
      `/notifications/logs${query ? `?${query}` : ''}`,
    );
  },
};

// ─── QuickBooks ───

export const quickbooksApi = {
  getAuthUrl: () => apiFetch<{ authUrl: string; mode: string }>('/quickbooks/auth-url'),
  getStatus: () => apiFetch<{
    connected: boolean;
    status?: string;
    companyName?: string;
    environment?: string;
    lastSyncAt?: string;
    tokenExpired?: boolean;
    syncLogs?: any[];
    mode: string;
  }>('/quickbooks/status'),
  disconnect: () => apiFetch<{ success: boolean }>('/quickbooks/disconnect', { method: 'POST' }),
  refreshToken: () => apiFetch<{ success: boolean }>('/quickbooks/refresh-token', { method: 'POST' }),
  syncTransactions: () => apiFetch<{ synced: number }>('/quickbooks/sync/transactions', { method: 'POST' }),
  syncExpenses: () => apiFetch<{ synced: number }>('/quickbooks/sync/expenses', { method: 'POST' }),
  getReport: (type: string, startDate: string, endDate: string) =>
    apiFetch<any>(`/quickbooks/reports?type=${type}&startDate=${startDate}&endDate=${endDate}`),
  getTransactions: (startDate: string, endDate: string) =>
    apiFetch<any[]>(`/quickbooks/transactions?startDate=${startDate}&endDate=${endDate}`),
};

export interface InstagramConnectionStatus {
  connectionId?: string | null;
  grantVerified?: boolean;
  reconnectRequired?: boolean;
  canSelectMedia?: boolean;
  reason?: string | null;
  capabilities?: { media: boolean; messages: boolean; comments: boolean; insights: boolean };
  subscription?: { status: 'confirmed' | 'pending' | 'failed' | 'not_required' | 'reconnect_required';
    desiredFields: string[]; confirmedFields: string[]; confirmedAt: string | null; canRetry: boolean };
  configured: boolean;
  connected: boolean;
  requestedScopes?: string[];
  username?: string;
  name?: string | null;
  accountType?: string | null;
  profilePictureUrl?: string | null;
  followersCount?: number | null;
  mediaCount?: number | null;
  importedMediaCount?: number;
  tokenExpiresAt?: string | null;
  tokenExpiring?: boolean;
  scopes?: string[];
  status?: string;
  lastSyncAt?: string | null;
  lastError?: string | null;
  connectedAt?: string;
}

export interface InstagramMediaItem {
  parentInstagramMediaId?: string | null;
  feedPostId?: string;
  feedVisibility?: 'AUTO' | 'SHOW' | 'HIDE';
  feedSelected?: boolean;
  feedBusinessIds?: string[];
  id: string;
  instagramMediaId: string;
  mediaType: string;
  caption: string | null;
  mediaUrl: string | null;
  thumbnailUrl: string | null;
  permalink: string | null;
  postedAt: string;
  likeCount: number | null;
  commentsCount: number | null;
  showOnStorefront: boolean;
  showOnMarketplace: boolean;
  sortOrder: number;
  syncedAt: string;
}

export interface InstagramInsights {
  available: boolean;
  note: string;
  metrics: Array<{
    name: string;
    title: string;
    description: string | null;
    period: string;
    values: Array<{ value: number | Record<string, number>; end_time?: string }>;
  }>;
}

export interface InstagramMediaPage {
  items: InstagramMediaItem[];
  nextCursor: string | null;
  selected: InstagramMediaItem | null;
}

export type InstagramInboxSession = { accessToken: string };
function inboxFetch<T>(path: string, businessId: string, options: ApiFetchOptions = {}, session?: InstagramInboxSession | null): Promise<T> {
  if (typeof businessId !== 'string' || !businessId.trim()) return Promise.reject(new Error('Select a business to open Instagram messages'));
  if (options.signal?.aborted) return Promise.reject(new DOMException('Instagram request cancelled', 'AbortError'));
  if (session !== undefined && (!session || !/^[A-Za-z0-9_.-]+$/.test(session.accessToken ?? ''))) return Promise.reject(new Error('Instagram session unavailable'));
  const boundAuth = session ? { authHeadersOverride: { Authorization: `Bearer ${session.accessToken}` }, _retried: true } : {};
  return apiFetch<T>(path, { ...options, ...boundAuth, businessId, skipGetCache: true, cache: 'no-store' });
}
const inboxCursor = (before?: string) => before ? `?${new URLSearchParams({ before })}` : '';
export interface InstagramProductPost {
  connectionId?: string | null;
  id: string; caption: string | null; previewUrl: string | null; permalink: string | null; showOnMarketplace: boolean;
}
const productPostsPath = (productId: string) => `/instagram/products/${encodeURIComponent(productId)}/posts`;
export const instagramProductPostsApi = {
  list: async (productId: string, businessId: string, session: InstagramInboxSession, signal?: AbortSignal) => {
    const result = await inboxFetch<{ posts: InstagramProductPost[] }>(productPostsPath(productId), businessId, { signal }, session);
    if (!Array.isArray(result?.posts) || result.posts.length > 10 || new Set(result.posts.map(p => p?.id)).size !== result.posts.length ||
      !result.posts.every(p => p && typeof p.id === 'string' && typeof p.showOnMarketplace === 'boolean' &&
        [p.caption, p.previewUrl, p.permalink].every(v => v === null || typeof v === 'string'))) throw new Error('Invalid product posts');
    return result.posts;
  },
  change: (productId: string, mediaId: string, method: 'POST' | 'DELETE', businessId: string, session: InstagramInboxSession, signal?: AbortSignal) =>
    inboxFetch<{ id: string }>(`${productPostsPath(productId)}/${encodeURIComponent(mediaId)}`, businessId, { method, signal }, session),
};

export const instagramInboxApi = {
  profile: (businessId: string, id: string, signal: AbortSignal | undefined, session: InstagramInboxSession | null) =>
    inboxFetch<{ id: string; username: string | null; profilePictureUrl: string | null }>(
      `/instagram/conversations/${encodeURIComponent(id)}/profile`, businessId, { signal }, session),
  importHistory: (businessId: string, session: InstagramInboxSession | null, cursor: string | null, signal?: AbortSignal) =>
    inboxFetch<unknown>('/instagram/inbox-import', businessId, { method: 'POST', body: JSON.stringify({ cursor }), signal }, session ?? null),
  setReplyPause: (businessId: string, id: string, optedOut: boolean, signal?: AbortSignal, session?: InstagramInboxSession | null) =>
    inboxFetch<{ id: string; optedOut: boolean }>(`/instagram/conversations/${encodeURIComponent(id)}/opt-out`, businessId,
      { method: 'PATCH', body: JSON.stringify({ optedOut }), signal }, session),
  orderLink: (businessId: string, id: string, signal?: AbortSignal, session?: InstagramInboxSession | null) =>
    inboxFetch<{ url: string; text: string; replyPolicy: InstagramThreadPage['replyPolicy'] }>(
      `/instagram/conversations/${encodeURIComponent(id)}/order-link`, businessId, { signal }, session),
  conversations: (businessId: string, before?: string, signal?: AbortSignal, session?: InstagramInboxSession | null) =>
    inboxFetch<InstagramConversationsPage>(`/instagram/conversations${inboxCursor(before)}`, businessId, { signal }, session),
  messages: (businessId: string, id: string, before?: string, signal?: AbortSignal, session?: InstagramInboxSession | null) =>
    inboxFetch<InstagramThreadPage>(`/instagram/conversations/${encodeURIComponent(id)}/messages${inboxCursor(before)}`, businessId, { signal }, session),
  read: (businessId: string, id: string, through: string, signal?: AbortSignal, session?: InstagramInboxSession | null) =>
    inboxFetch<{ id: string; readAt: string }>(`/instagram/conversations/${encodeURIComponent(id)}/read`, businessId,
      { method: 'POST', body: JSON.stringify({ through }), signal }, session),
  reply: (businessId: string, id: string, clientRequestId: string, text: string, signal?: AbortSignal, session?: InstagramInboxSession | null) =>
    inboxFetch<{ id: string; deliveryStatus: string; alreadyQueued: boolean }>(`/instagram/conversations/${encodeURIComponent(id)}/messages`, businessId,
      { method: 'POST', body: JSON.stringify({ clientRequestId, text }), signal }, session),
  failures: (businessId: string, before?: string, signal?: AbortSignal, session?: InstagramInboxSession | null) =>
    inboxFetch<InstagramFailurePage>(`/instagram/inbox-failures${inboxCursor(before)}`, businessId, { signal }, session),
  retryReceipt: (businessId: string, id: string, signal?: AbortSignal, session?: InstagramInboxSession | null) =>
    inboxFetch<{ id: string; retryScheduled: true }>(`/instagram/inbox-failures/${encodeURIComponent(id)}/retry`, businessId, { method: 'POST', signal }, session),
};

export const instagramCommentsApi = {
  list: (businessId: string, session: InstagramInboxSession | null, before?: string, signal?: AbortSignal) =>
    inboxFetch<unknown>(`/instagram/comments${inboxCursor(before)}`, businessId, { signal }, session ?? null).then(readOwnerInstagramComments),
  replies: async (businessId: string, session: InstagramInboxSession | null, id: string, before?: string, signal?: AbortSignal) =>
    readInstagramCommentReplies(await inboxFetch<unknown>(`/instagram/comments/${commentUuid(id)}/replies${inboxCursor(before)}`, businessId, { signal }, session ?? null), id),
  reply: async (businessId: string, session: InstagramInboxSession | null, id: string, clientRequestId: string, text: string, signal?: AbortSignal) =>
    readInstagramCommentReply(await inboxFetch<unknown>(`/instagram/comments/${commentUuid(id)}/replies`, businessId,
      { method: 'POST', body: JSON.stringify({ clientRequestId: commentUuid(clientRequestId), text }), signal }, session ?? null)),
  publication: async (businessId: string, session: InstagramInboxSession | null, id: string, channel: CommentChannel, signal?: AbortSignal) =>
    readInstagramCommentPublication(await inboxFetch<unknown>(`/instagram/comments/${commentUuid(id)}/publication/${commentChannel(channel)}`, businessId, { signal }, session ?? null), id, channel),
  approve: async (businessId: string, session: InstagramInboxSession | null, review: CommentPublication, rightsConfirmed: true, signal?: AbortSignal) => {
    if (!review.canApprove || !review.fingerprint || !review.quote || rightsConfirmed !== true) throw new Error('Review the current original comment and confirm rights');
    const result = readInstagramCommentPublication(await inboxFetch<unknown>(`/instagram/comments/${commentUuid(review.commentId)}/publication/${commentChannel(review.channel)}`, businessId,
      { method: 'POST', body: JSON.stringify({ connectionId: commentUuid(review.connectionId), fingerprint: review.fingerprint, rightsConfirmed }), signal }, session ?? null), review.commentId, review.channel);
    if (!result.approved || result.connectionId !== review.connectionId || result.fingerprint !== review.fingerprint) throw new Error('Instagram approval was not confirmed; reload its review');
    return result;
  },
  withdraw: async (businessId: string, session: InstagramInboxSession | null, review: CommentPublication, signal?: AbortSignal) => {
    const result = readInstagramCommentPublication(await inboxFetch<unknown>(`/instagram/comments/${commentUuid(review.commentId)}/publication/${commentChannel(review.channel)}`, businessId,
      { method: 'DELETE', body: JSON.stringify({ connectionId: commentUuid(review.connectionId) }), signal }, session ?? null), review.commentId, review.channel);
    if (result.approved || result.quote !== null || result.connectionId !== review.connectionId) throw new Error('Instagram withdrawal was not confirmed; reload its review');
    return result;
  },
  refresh: async (businessId: string, session: InstagramInboxSession | null, id: string, connectionId: string, signal?: AbortSignal) =>
    readInstagramCommentRefresh(await inboxFetch<unknown>(`/instagram/comments/${commentUuid(id)}/refresh-publication`, businessId,
      { method: 'POST', body: JSON.stringify({ connectionId: commentUuid(connectionId) }), signal }, session ?? null)),
  failures: (businessId: string, session: InstagramInboxSession | null, before?: string, signal?: AbortSignal) =>
    inboxFetch<unknown>(`/instagram/comments/failures${inboxCursor(before)}`, businessId, { signal }, session ?? null).then(readInstagramCommentFailures),
  retryFailure: async (businessId: string, session: InstagramInboxSession | null, id: string, signal?: AbortSignal) =>
    readInstagramCommentRetry(await inboxFetch<unknown>(`/instagram/comments/failures/${commentUuid(id)}/retry`, businessId, { method: 'POST', signal }, session ?? null), id),
};

export interface InstagramAutomationSettings {
  connectionId: string | null; instagramAccountId: string | null; username: string | null;
  enabled: boolean; canEnable: boolean;
  reason: null | 'not_connected' | 'reconnect_required' | 'permission_required' | 'ordering_site_required';
}
export const instagramAutomationApi = {
  get: (businessId: string, session: InstagramInboxSession | null, signal?: AbortSignal) =>
    inboxFetch<InstagramAutomationSettings>('/instagram/order-link-automation', businessId, { signal }, session ?? null),
  save: (businessId: string, session: InstagramInboxSession | null, observed: { connectionId: string; instagramAccountId: string }, enabled: boolean, signal?: AbortSignal) =>
    inboxFetch<InstagramAutomationSettings>('/instagram/order-link-automation', businessId,
      { method: 'PATCH', body: JSON.stringify({ ...observed, enabled }), signal }, session ?? null),
};

export const instagramMenuDraftApi = {
  list: (businessId: string, session: InstagramInboxSession | null, before?: string, signal?: AbortSignal) =>
    inboxFetch<unknown>(`/instagram/menu-drafts${inboxCursor(before)}`, businessId, { signal }, session ?? null).then(readInstagramMenuDraftPage),
  media: (businessId: string, session: InstagramInboxSession | null, cursor?: string, signal?: AbortSignal) =>
    inboxFetch<unknown>(`/instagram/media/page${cursor ? `?${new URLSearchParams({ cursor })}` : ''}`, businessId, { signal }, session ?? null).then(readInstagramMenuMediaPage),
  import: async (businessId: string, session: InstagramInboxSession | null, connectionId: string, mediaId: string, signal?: AbortSignal) => {
    const draft = readInstagramMenuDraft(await inboxFetch<unknown>('/instagram/menu-drafts', businessId,
      { method: 'POST', body: JSON.stringify({ connectionId, mediaId }), signal }, session ?? null));
    if (draft.connectionId !== connectionId || draft.mediaId !== mediaId) throw new Error('Instagram menu source changed');
    return draft;
  },
  edit: async (businessId: string, session: InstagramInboxSession | null, current: InstagramMenuDraft,
    title: string | null, category: string | null, signal?: AbortSignal) => {
    const draft = readInstagramMenuDraft(await inboxFetch<unknown>(`/instagram/menu-drafts/${encodeURIComponent(current.id)}`, businessId,
      { method: 'PATCH', body: JSON.stringify({ revision: current.revision, title, category }), signal }, session ?? null));
    if (draft.id !== current.id || draft.connectionId !== current.connectionId || draft.mediaId !== current.mediaId
      || draft.revision !== current.revision + 1 || draft.title !== title || draft.category !== category) throw new Error('Instagram menu draft changed');
    return draft;
  },
  remove: async (businessId: string, session: InstagramInboxSession | null, id: string, signal?: AbortSignal) => {
    const result = await inboxFetch<{ deleted?: unknown }>(`/instagram/menu-drafts/${encodeURIComponent(id)}`, businessId, { method: 'DELETE', signal }, session ?? null);
    if (!result || result.deleted !== true) throw new Error('Instagram draft deletion was not confirmed');
    return { deleted: true as const };
  },
};

export interface InstagramStoreConnection { id: string; name: string; connectionId: string | null; username: string | null }

export const instagramApi = {
  getStoreConnections: (businessId: string, signal?: AbortSignal, session: InstagramInboxSession | null = null) =>
    inboxFetch<{ stores: InstagramStoreConnection[] }>('/instagram/connection/store', businessId, { signal }, session).then(result => {
      if (!Array.isArray(result.stores) || result.stores.some(store => !store || typeof store.id !== 'string' || typeof store.name !== 'string'
        || !(store.connectionId === null || typeof store.connectionId === 'string') || !(store.username === null || typeof store.username === 'string'))) {
        throw new Error('Could not confirm connected stores');
      }
      return result;
    }),
  moveStoreConnection: (businessId: string, connectionId: string, targetBusinessId: string, signal?: AbortSignal, session: InstagramInboxSession | null = null) =>
    inboxFetch<{ businessId: string; connectionId: string; username: string }>('/instagram/connection/store', businessId,
      { method: 'POST', body: JSON.stringify({ connectionId, targetBusinessId }), signal }, session),
  retrySubscription: (businessId: string, connectionId: string, signal?: AbortSignal, session: InstagramInboxSession | null = null) =>
    inboxFetch<{ status: string; fields?: string[] }>('/instagram/webhook-subscription', businessId,
      { method: 'POST', body: JSON.stringify({ connectionId }), signal }, session),
  getConnectUrl: (businessId: string, signal?: AbortSignal, session: InstagramInboxSession | null = null) =>
    inboxFetch<{
      url: string;
      scopes: string[];
      accountRequirement: string;
    }>('/instagram/connect', businessId, { signal }, session),
  getStatus: (businessId: string, signal?: AbortSignal, session: InstagramInboxSession | null = null) =>
    inboxFetch<InstagramConnectionStatus>('/instagram/status', businessId, { signal }, session),
  getMedia: (businessId?: string, signal?: AbortSignal) =>
    apiFetch<InstagramMediaItem[]>('/instagram/media', {
      skipGetCache: true, businessId, signal,
    }),
  getMediaPage: (businessId: string, options: { cursor?: string; selectedId?: string | null; signal?: AbortSignal; session?: InstagramInboxSession | null } = {}) => {
    const query = new URLSearchParams();
    if (options.cursor) query.set('cursor', options.cursor);
    if (options.selectedId) query.set('selectedId', options.selectedId);
    return inboxFetch<InstagramMediaPage>(`/instagram/media/page${query.size ? `?${query}` : ''}`, businessId,
      { signal: options.signal }, options.session);
  },
  setProductPhoto: (productId: string, mediaId: string | null, businessId: string, signal?: AbortSignal) =>
    apiFetch<{ instagramPhotoId: string | null }>(`/instagram/products/${productId}/photo`, {
      method: 'PATCH', body: JSON.stringify({ mediaId }), businessId, signal,
    }),
  getInsights: (businessId: string, signal?: AbortSignal, session: InstagramInboxSession | null = null) =>
    inboxFetch<InstagramInsights>('/instagram/insights', businessId, { signal }, session),
  sync: (businessId: string, signal?: AbortSignal, session: InstagramInboxSession | null = null) =>
    inboxFetch<{ imported: number; lastSyncAt: string | null }>('/instagram/sync', businessId, { method: 'POST', signal }, session),
  updateMedia: (id: string, showOnStorefront: boolean, businessId?: string, signal?: AbortSignal, session?: InstagramInboxSession | null) =>
    inboxFetch<{ id: string; showOnStorefront: boolean }>(`/instagram/media/${id}`, businessId ?? '',
      { method: 'PATCH', body: JSON.stringify({ showOnStorefront }), signal }, session),
  updateMarketplaceApproval: (id: string, approved: boolean, businessId?: string, signal?: AbortSignal, session?: InstagramInboxSession | null) =>
    inboxFetch<{ id: string; showOnMarketplace: boolean }>(`/instagram/media/${id}/marketplace`, businessId ?? '',
      { method: 'PATCH', body: JSON.stringify({ approved }), signal }, session),
  disconnect: (businessId: string, signal?: AbortSignal, session: InstagramInboxSession | null = null) =>
    inboxFetch<{ success: boolean; deletedImportedMedia: boolean }>('/instagram/connection', businessId, { method: 'DELETE', signal }, session),
  getDeletionStatus: (confirmationCode: string) =>
    apiFetch<{
      confirmationCode: string;
      status: string;
      requestedAt: string;
      completedAt: string;
    }>(`/instagram/data-deletion/status/${confirmationCode}`, {
      skipGetCache: true,
    }),
};

// ─── Invoices ───

export const invoicesApi = {
  list: (params?: { status?: string; page?: number; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    const query = qs.toString();
    return apiFetch<{ data: any[]; meta: { total: number; page: number; limit: number; totalPages: number } }>(
      `/invoices${query ? `?${query}` : ''}`,
    );
  },
  get: (id: string) => apiFetch<any>(`/invoices/${id}`),
  create: (data: {
    customerName: string;
    customerEmail?: string;
    customerAddress?: string;
    lineItems: { description: string; quantity: number; unitPrice: number; amount: number }[];
    notes?: string;
    terms?: string;
    taxRate?: number;
    issueDate: string;
    dueDate: string;
  }) => apiFetch<any>('/invoices', { method: 'POST', body: JSON.stringify(data) }),
  generateFromOffer: (offerId: string) =>
    apiFetch<any>(`/invoices/from-offer/${offerId}`, { method: 'POST' }),
  update: (id: string, data: {
    customerName?: string;
    customerEmail?: string;
    notes?: string;
    terms?: string;
    status?: string;
  }) => apiFetch<any>(`/invoices/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  markPaid: (id: string) => apiFetch<any>(`/invoices/${id}/mark-paid`, { method: 'POST' }),
  syncToQB: (id: string) => apiFetch<any>(`/invoices/${id}/sync-to-qb`, { method: 'POST' }),
};

export interface ConnectStatusView {
  configured: boolean;
  liveMode: boolean;
  status: 'none' | 'onboarding' | 'restricted' | 'enabled';
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  requirementsDue: string[];
}

/** Stripe Connect (seller payouts). Onboarding itself happens on Stripe-hosted pages. */
export const paymentsApi = {
  connectStatus: (refresh = false) =>
    apiFetch<ConnectStatusView>(`/payments/connect/status${refresh ? '?refresh=1' : ''}`),
  connectOnboard: () => apiFetch<{ url: string }>('/payments/connect/onboard', { method: 'POST' }),
  connectDashboardLink: () => apiFetch<{ url: string }>('/payments/connect/dashboard-link', { method: 'POST' }),
};

export type BillingPlan = 'free' | 'plus' | 'pro';
export type PaidBillingPlan = Exclude<BillingPlan, 'free'>;
export type BillingInterval = 'month' | 'year';

export interface BillingStatusView {
  plan: BillingPlan;
  effectivePlan: BillingPlan;
  isFounding: boolean;
  canManage: boolean;
  foundingSeq: number | null;
  foundingUntil: string | null;
  hasCustomer: boolean;
  subscriptionId: string | null;
  subscriptionStatus: string | null;
  currentPeriodEnd: string | null;
  billingInterval: BillingInterval | null;
  graceEndsAt: string | null;
}

/** Sweetspot plan subscription. Checkout and account changes happen on Stripe-hosted pages. */
export const billingApi = {
  status: (refresh = false) =>
    apiFetch<BillingStatusView>('/billing/status', { skipGetCache: refresh }),
  checkout: (plan: PaidBillingPlan, interval: BillingInterval) =>
    apiFetch<{ sessionId: string; url: string }>('/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({ plan, interval }),
    }),
  portal: () => apiFetch<{ url: string }>('/billing/portal', { method: 'POST' }),
};

export interface StorefrontVersionRow {
  id: string;
  label: string | null;
  status: 'draft' | 'published' | 'archived' | string;
  createdAt: string;
  updatedAt: string;
}
export interface StorefrontSiteState {
  site: { id: string; publishedVersionId: string | null };
  versions: StorefrontVersionRow[];
  history: { limit: number | null; retained: number };
}

/** `POST /storefront-builder/ai/edit` — a proposal: ops + the document they produce. Nothing is saved until applyOps. */
export interface StorefrontAiEdit {
  ops: unknown[];
  summary: string;
  doc: any;
  changed: { tokens: string[]; sections: string[] };
  attempts: number;
  versionId: string | null;
  versionStatus: string | null;
}

export interface StorefrontThemeProposal {
  name: string;
  mood: string;
  tokens: Record<string, string | boolean>;
  copy: { headline?: string; tagline?: string; about?: string };
  /** Complete document — save it with createVersion. */
  doc: any;
}

export interface StorefrontAiTheme {
  proposals: StorefrontThemeProposal[];
  basedOn: string;
  inputs: { images: number; products: number };
  attempts: number;
}

/** Storefront builder: version documents for MY store (Design tab). */
export const storefrontBuilderApi = {
  getSite: () => apiFetch<StorefrontSiteState>('/storefront-builder/site'),
  getVersion: (id: string) => apiFetch<any>(`/storefront-builder/versions/${id}`),
  createVersion: (doc: unknown, label?: string) =>
    apiFetch<any>('/storefront-builder/versions', { method: 'POST', body: JSON.stringify({ doc, label }) }),
  updateVersion: (id: string, data: { doc?: unknown; label?: string }) =>
    apiFetch<any>(`/storefront-builder/versions/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  /** Apply ops to a version: a draft changes in place, a published one yields a new draft (`created`). */
  applyOps: (id: string, ops: unknown[], label?: string) =>
    apiFetch<{ version: any; changed: { tokens: string[]; sections: string[] }; created: boolean; ops: number }>(
      `/storefront-builder/versions/${id}/ops`,
      { method: 'POST', body: JSON.stringify({ ops, label }) },
    ),
  aiEdit: (data: { message: string; versionId?: string; doc?: unknown; selection?: string }) =>
    apiFetch<StorefrontAiEdit>('/storefront-builder/ai/edit', { method: 'POST', body: JSON.stringify(data) }),
  aiTheme: (data: { brief?: string; templateId?: string; count?: number } = {}) =>
    apiFetch<StorefrontAiTheme>('/storefront-builder/ai/theme', { method: 'POST', body: JSON.stringify(data) }),
  publish: (id: string) => apiFetch<StorefrontSiteState>(`/storefront-builder/versions/${id}/publish`, { method: 'POST' }),
  rollback: (id: string) => apiFetch<StorefrontSiteState>(`/storefront-builder/versions/${id}/rollback`, { method: 'POST' }),
  unpublish: () => apiFetch<StorefrontSiteState>('/storefront-builder/unpublish', { method: 'POST' }),
};

export const bankingApi = {
  createFinancialConnectionsSession: () =>
    apiFetch<{ clientSecret: string; sessionId: string; publishableKey: string; mode: string }>(
      '/banking/financial-connections-session',
      { method: 'POST' },
    ),
  linkAccounts: (data: {
    sessionId: string;
    accounts: Array<{
      id: string;
      displayName?: string;
      institutionName: string;
      last4?: string;
      category?: string;
      subcategory?: string;
    }>;
  }) =>
    apiFetch<{ success: boolean }>('/banking/link-accounts', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  getAccounts: () => apiFetch<any[]>('/banking/accounts'),
  removeAccount: (itemId: string) => apiFetch<{ success: boolean }>(`/banking/accounts/${itemId}`, { method: 'DELETE' }),
  sync: () => apiFetch<{ synced: number; classified: number }>('/banking/sync', { method: 'POST' }),
  getTransactions: (params?: { month?: string; classification?: string; page?: number; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.month) qs.set('month', params.month);
    if (params?.classification) qs.set('classification', params.classification);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    const query = qs.toString();
    return apiFetch<{ data: any[]; meta: { total: number; page: number; limit: number; totalPages: number } }>(`/banking/transactions${query ? `?${query}` : ''}`);
  },
  updateClassification: (id: string, data: { classification: string; linkedIngredient?: string }) => apiFetch<any>(`/banking/transactions/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  getSummary: (month?: string) => apiFetch<{ totalCogs: number; totalRevenue: number; totalPackaging: number; totalOverhead: number; totalOther: number; netProfit: number; transactionCount: number }>(`/banking/summary${month ? `?month=${month}` : ''}`),
};

// ─── Ratings ───

export interface BusinessRatingItem {
  id: string;
  offerId: string | null;
  raterBusinessId: string;
  rateeBusinessId: string;
  role: 'buyer' | 'seller';
  score: number;
  comment: string | null;
  createdAt: string;
  rater: { id: string; name: string; avatarUrl: string | null };
  productLinks?: Array<{ product: { id: string; title: string } }>;
}

export interface BusinessRatingSummary {
  avgRating: number | null;
  ratingCount: number;
  distribution: Record<number, number>;
  data: BusinessRatingItem[];
  meta: { total: number; limit: number; offset: number };
}

export interface RatingStatus {
  canRate: boolean;
  alreadyRated: boolean;
  offerStatus: string;
  role: 'buyer' | 'seller';
  existingRating: BusinessRatingItem | null;
}

export const ratingsApi = {
  submit: (data: { offerId: string; score: number; comment?: string }) =>
    apiFetch<BusinessRatingItem>('/ratings', { method: 'POST', body: JSON.stringify(data) }),

  getStatus: (offerId: string) =>
    apiFetch<RatingStatus>(`/ratings/status/${offerId}`),

  getForBusiness: (businessId: string, params?: { limit?: number; offset?: number }, options?: ApiFetchOptions) => {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.offset) qs.set('offset', String(params.offset));
    return apiFetch<BusinessRatingSummary>(`/businesses/${businessId}/ratings${qs.toString() ? `?${qs}` : ''}`, options);
  },
};

// ─── Wholesale ───

export interface BuyerProfile {
  buyer: {
    id: string;
    name: string;
    slug: string | null;
    avatarUrl: string | null;
    location: string;
    bio: string | null;
  };
  products: Array<{
    id: string;
    title: string;
    price: number;
    unit: string;
    category: string | null;
    imageUrl: string | null;
    customPrice: number | null;
    discountPercent: number | null;
    listPriceAtTime: number | null;
  }>;
  priceList: { id: string; name: string; discountType: string; discountValue: number } | null;
  stats: {
    totalSpent: number;
    avgOrderValue: number;
    orderCount: number;
    lastOrderDate: string | null;
    lastOrderDaysAgo: number | null;
  };
  segment: 'vip' | 'regular' | 'new' | 'at_risk' | 'dormant';
  healthScore: number;
  healthTrend: 'up' | 'down' | 'stable';
  reorderPattern: {
    avgIntervalDays: number;
    isOverdue: boolean;
    overdueDays: number | null;
    confidence: string;
    nextExpectedDate?: string | null;
  } | null;
  topProducts: Array<{
    id: string;
    title: string;
    imageUrl: string | null;
    orderCount: number;
    totalRevenue: number;
  }>;
  notes: string | null;
}

export interface ActivityItem {
  id: string;
  type: 'order_completed' | 'message_sent' | 'invoice_sent' | 'offer_created';
  date: string;
  summary: string;
  metadata?: {
    amount?: number;
    product?: string;
    [key: string]: any;
  };
}

export interface WholesaleCustomer {
  id: string;
  name: string;
  slug: string | null;
  avatarUrl: string | null;
  location: string;
  discountedProductCount: number;
  totalSpent: number;
  lastOrderDate: string | null;
  orderCount: number;
  priceList: { id: string; name: string; discountType: string; discountValue: number } | null;
  segment: 'vip' | 'regular' | 'new' | 'at_risk' | 'dormant';
  healthScore: number;
  healthTrend: 'up' | 'down' | 'stable';
  avgOrderInterval: number | null;
  lastOrderDaysAgo: number | null;
  reorderPattern: {
    avgIntervalDays: number;
    isOverdue: boolean;
    overdueDays: number | null;
    confidence: string;
  } | null;
}

export interface SegmentCounts {
  vip: number;
  regular: number;
  new: number;
  at_risk: number;
  dormant: number;
  total: number;
}

export interface OverdueBuyer {
  id: string;
  name: string;
  avatarUrl: string | null;
  avgIntervalDays: number;
  overdueDays: number;
  expectedDate: string;
  lastOrderDate: string;
  totalSpent: number;
  segment: string;
}

export interface WholesaleCustomerDetail {
  buyer: {
    id: string;
    name: string;
    slug: string | null;
    avatarUrl: string | null;
    location: string;
    bio: string | null;
  };
  products: Array<{
    id: string;
    title: string;
    price: number;
    unit: string;
    category: string | null;
    imageUrl: string | null;
    customPrice: number | null;
    discountPercent: number | null;
    listPriceAtTime: number | null;
  }>;
  priceList: { id: string; name: string; discountType: string; discountValue: number } | null;
}

export interface MessageTemplate {
  id: string;
  sellerBusinessId: string;
  name: string;
  body: string;
  isDefault: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface PersonalizedPrices {
  priceList: { discountType: string; discountValue: number } | null;
  productPrices: Record<string, { agreedPrice: number; discountPercent: number; listPriceAtTime: number }>;
}

export interface CrmSummary {
  ordersThisWeek: number;
  revenueThisWeek: number;
  activeBuyersThisWeek: number;
  unreadAlerts: number;
  topAlerts: CrmAlert[];
  segmentCounts: { vip: number; regular: number; new: number; at_risk: number; dormant: number; total: number };
}

export interface CrmAlert {
  id: string;
  sellerBusinessId: string;
  buyerBusinessId: string | null;
  alertType: string;
  title: string;
  body: string | null;
  status: string;
  metadata: any;
  createdAt: string;
  readAt: string | null;
  dismissedAt: string | null;
}

export interface CrmSettings {
  sellerBusinessId: string;
  digestEnabled: boolean;
  digestFrequency: string;
  alertChurnEnabled: boolean;
  alertAnomalyEnabled: boolean;
  alertReminderEnabled: boolean;
  maxRemindersPerDay: number;
}

export interface DeliveryPrefs {
  preferredDeliveryDay: number | null;
  deliveryWindow: string | null;
  deliveryAddress: string | null;
  deliveryInstructions: string | null;
}

export interface SellerSummary {
  sellerBusinessId: string;
  name: string;
  avatarUrl: string | null;
  slug: string | null;
  locationCity: string | null;
  totalOrders: number;
  totalSpent: number;
  lastOrderDate: string;
  firstOrderDate: string;
  invoiceCount: number;
  paidInvoiceCount: number;
}

export interface SellerProfile {
  seller: { id: string; name: string; avatarUrl: string | null; coverImageUrl: string | null; slug: string | null; locationCity: string | null; bio: string | null };
  stats: { totalOrders: number; totalSpent: number; firstOrderDate: string | null; lastOrderDate: string | null };
  frequentProducts: Array<{ id: string; title: string; imageUrl: string | null; count: number; lastOrdered: string }>;
  recentOrders: Array<{ id: string; product: string; quantity: number; unit: string | null; price: number; date: string }>;
  outstandingAmount: number;
  outstandingInvoiceCount: number;
  priceList: { discountType: string; discountValue: number; preferredDeliveryDay: number | null; deliveryWindow: string | null } | null;
}

export interface FavoriteProduct {
  id: string;
  buyerBusinessId: string;
  productId: string;
  createdAt: string;
  product: { id: string; title: string; price: number; unit: string | null; imageUrl: string | null; isActive: boolean; sellerBusinessId: string; sellerBusiness: { name: string; avatarUrl: string | null } };
}

export interface Negotiation {
  offerId: string;
  status: string;
  role: 'buyer' | 'seller';
  product: { id: string; title: string; imageUrl: string | null } | null;
  price: number;
  quantity: number;
  unit: string | null;
  otherParty: { businessId: string; name: string; avatarUrl: string | null };
  daysPending: number;
  createdAt: string;
}

export interface PaymentAnalytics {
  totalInvoices: number;
  totalBilled: number;
  totalPaid: number;
  outstandingBalance: number;
  outstandingInvoiceCount: number;
  avgDaysToPayment: number | null;
  paymentReliability: number | null;
  creditLimit: number | null;
  recentInvoices: Array<{ id: string; total: number; status: string; issuedAt: string; paidAt: string | null }>;
}

export interface HealthSnapshot {
  snapshotDate: string;
  score: number;
  recencyScore: number;
  frequencyScore: number;
  monetaryScore: number;
  engagementScore: number;
}

export interface PurchaseAnalytics {
  period: { start: string; end: string; weeks: number };
  summary: { totalRevenue: number; totalOrders: number; uniqueBuyers: number; avgOrderValue: number };
  weeklyTrend: Array<{ weekStart: string; orders: number; revenue: number; uniqueBuyers: number }>;
  productMix: Array<{ id: string; title: string; revenue: number; orderCount: number }>;
  topBuyers: Array<{ buyerBusinessId: string; name: string; avatarUrl: string | null; revenue: number; orderCount: number }>;
}

export interface StorefrontAnalytics {
  period: { start: string; end: string; days: number };
  summary: { siteViews: number; ctaClicks: number; orderStarts: number };
  topProducts: Array<{ id: string; title: string; orderStarts: number }>;
}

export interface OrderTemplate {
  id: string;
  buyerBusinessId: string;
  sellerBusinessId: string;
  name: string;
  items: Array<{ productId: string; quantity: number; unit?: string }>;
  createdAt: string;
  updatedAt: string;
}

export interface BuyerLtv {
  lifetimeRevenue: number;
  projectedAnnualValue: number;
  monthlyTrend: Array<{ month: string; revenue: number; orders: number }>;
  avgOrderValue: number;
  orderCount: number;
}

export interface OnboardingChecklist {
  steps: Array<{ key: string; label: string; completedAt: string | null; isComplete: boolean }>;
  isComplete: boolean;
}

export interface ProductAffinity {
  affinities: Array<{ productA: { id: string; title: string }; productB: { id: string; title: string }; coOccurrences: number; confidence: number }>;
  totalBaskets: number;
}

export interface SeasonalPattern {
  patterns: Array<{ month: string; revenue: number; percentChange: number }>;
  hasEnoughData: boolean;
  avgMonthlyRevenue?: number;
}

export interface SuggestedActionItem {
  id: string;
  sellerBusinessId: string;
  buyerBusinessId: string | null;
  actionType: string;
  title: string;
  body: string | null;
  ctaLabel: string | null;
  ctaHref: string | null;
  status: string;
  createdAt: string;
}

export interface PlatformBenchmarks {
  available: boolean;
  totalSellers: number;
  message?: string;
  period?: string;
  you?: { revenue: number; buyerCount: number; orderCount: number };
  platformAvg?: { revenue: number; buyerCount: number; orderCount: number };
}

export interface LosingBuyer {
  buyerBusinessId: string;
  alertType: string;
  title: string;
  name: string;
  avatarUrl: string | null;
  createdAt: string;
}

export const wholesaleApi = {
  getCustomers: (params?: { segment?: string; search?: string; sort?: string; order?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.segment && params.segment !== 'all') searchParams.set('segment', params.segment);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.sort) searchParams.set('sort', params.sort);
    if (params?.order) searchParams.set('order', params.order);
    const qs = searchParams.toString();
    return apiFetch<WholesaleCustomer[]>(`/wholesale/customers${qs ? `?${qs}` : ''}`);
  },

  getSegmentCounts: () =>
    apiFetch<SegmentCounts>('/wholesale/customers/segments/counts'),

  getOverdueBuyers: () =>
    apiFetch<OverdueBuyer[]>('/wholesale/customers/overdue'),

  getCustomerDetail: (buyerBusinessId: string) =>
    apiFetch<WholesaleCustomerDetail>(`/wholesale/customers/${buyerBusinessId}`),

  updatePriceList: (buyerBusinessId: string, data: {
    name?: string;
    discountType?: string;
    discountValue?: number;
    priceOverrides?: Array<{ productId: string; agreedPrice: number }>;
  }) =>
    apiFetch<any /* TODO: type the price list update response */>(`/wholesale/customers/${buyerBusinessId}/prices`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  applyBulkDiscount: (buyerBusinessId: string, discountPercent: number) =>
    apiFetch<{ appliedTo: number; discountPercent: number }>(`/wholesale/customers/${buyerBusinessId}/bulk-discount`, {
      method: 'POST',
      body: JSON.stringify({ discountPercent }),
    }),

  inviteBuyer: (data: { email: string; businessName?: string }) =>
    apiFetch<any /* TODO: type the invite response */>('/wholesale/invite', { method: 'POST', body: JSON.stringify(data) }),

  getInvitations: () =>
    apiFetch<any[] /* TODO: type the invitation list items */>('/wholesale/invitations'),

  cancelInvitation: (id: string) =>
    apiFetch<any /* TODO: type the cancel invitation response */>(`/wholesale/invitations/${id}`, { method: 'DELETE' }),

  getInvitationByToken: (token: string) =>
    apiFetch<any /* TODO: type the invitation token response */>(`/wholesale/invite/${token}`),

  acceptInvitation: (token: string) =>
    apiFetch<any /* TODO: type the accept invitation response */>(`/wholesale/invite/${token}/accept`, { method: 'POST' }),

  getPersonalizedPrices: (sellerBusinessId: string) =>
    apiFetch<PersonalizedPrices>(`/marketplace/discounts/${sellerBusinessId}`),

  getOrderHistory: () => apiFetch<any[]>('/wholesale/orders'),

  getReorderData: (offerId: string) =>
    apiFetch<any>(`/wholesale/orders/${offerId}/reorder`),

  getCutoffs: () =>
    apiFetch<any[]>('/wholesale/cutoffs'),

  updateCutoffs: (data: { cutoffs: any[]; timezone?: string }) =>
    apiFetch<any>('/wholesale/cutoffs', { method: 'PUT', body: JSON.stringify(data) }),

  getNextCutoff: (sellerBusinessId: string) =>
    apiFetch<{ cutoffTime: string; deliveryDate: string; dayOfWeek: number; timezone: string; isOpen: boolean } | null>(
      `/wholesale/cutoffs/${sellerBusinessId}/next`,
    ),

  getInvoices: () => apiFetch<any[]>('/wholesale/invoices'),

  getInvoice: (id: string) => apiFetch<any>(`/wholesale/invoices/${id}`),

  downloadInvoicePdf: (id: string) => {
    window.open(`/api/proxy/wholesale/invoices/${id}/pdf`, '_blank');
  },

  sendInvoiceEmail: (id: string) => apiFetch<any>(`/wholesale/invoices/${id}/send`, { method: 'POST' }),

  generateInvoice: (offerId: string) => apiFetch<any>(`/wholesale/invoices/generate/${offerId}`, { method: 'POST' }),

  // Standing Orders
  getStandingOrders: () => apiFetch<any[]>('/wholesale/standing-orders'),

  getStandingOrder: (id: string) => apiFetch<any>(`/wholesale/standing-orders/${id}`),

  createStandingOrder: (data: any) =>
    apiFetch<any>('/wholesale/standing-orders', { method: 'POST', body: JSON.stringify(data) }),

  updateStandingOrder: (id: string, data: any) =>
    apiFetch<any>(`/wholesale/standing-orders/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  pauseStandingOrder: (id: string) =>
    apiFetch<any>(`/wholesale/standing-orders/${id}/pause`, { method: 'POST' }),

  resumeStandingOrder: (id: string) =>
    apiFetch<any>(`/wholesale/standing-orders/${id}/resume`, { method: 'POST' }),

  cancelStandingOrder: (id: string) =>
    apiFetch<any>(`/wholesale/standing-orders/${id}/cancel`, { method: 'POST' }),

  skipStandingOrder: (id: string) =>
    apiFetch<any>(`/wholesale/standing-orders/${id}/skip`, { method: 'POST' }),

  // Delivery Zones
  getDeliveryZones: () =>
    apiFetch<any[]>('/wholesale/delivery-zones'),

  createDeliveryZone: (data: { name: string; zipCodes?: string[] }) =>
    apiFetch<any>('/wholesale/delivery-zones', { method: 'POST', body: JSON.stringify(data) }),

  updateDeliveryZone: (id: string, data: any) =>
    apiFetch<any>(`/wholesale/delivery-zones/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteDeliveryZone: (id: string) =>
    apiFetch<any>(`/wholesale/delivery-zones/${id}`, { method: 'DELETE' }),

  checkDeliveryZone: (sellerBusinessId: string, zip: string) =>
    apiFetch<any>(`/wholesale/delivery-zones/${sellerBusinessId}/check?zip=${zip}`),

  // Analytics & CRM
  getAnalyticsDashboard: () =>
    apiFetch<any>('/wholesale/analytics/dashboard'),

  getCustomerCrm: (buyerBusinessId: string) =>
    apiFetch<any>(`/wholesale/analytics/customers/${buyerBusinessId}`),

  getPurchaseAnalytics: () =>
    apiFetch<PurchaseAnalytics>('/wholesale/analytics/purchases'),

  getBuyerProfile: (buyerBusinessId: string) =>
    apiFetch<BuyerProfile>(`/wholesale/customers/${buyerBusinessId}/profile`),

  updateBuyerNotes: (buyerBusinessId: string, notes: string) =>
    apiFetch<{ notes: string; updatedAt: string }>(`/wholesale/customers/${buyerBusinessId}/notes`, {
      method: 'PATCH',
      body: JSON.stringify({ notes }),
    }),

  getBuyerActivities: (buyerBusinessId: string, cursor?: string, limit?: number) => {
    const params = new URLSearchParams();
    params.set('limit', String(limit ?? 20));
    if (cursor) params.set('cursor', cursor);
    return apiFetch<{ items: ActivityItem[]; nextCursor: string | null }>(
      `/wholesale/customers/${buyerBusinessId}/activities?${params.toString()}`,
    );
  },

  sendReorderReminder: (buyerBusinessId: string, templateId?: string) =>
    apiFetch<{ success: boolean; conversationId: string }>(`/wholesale/customers/${buyerBusinessId}/remind`, {
      method: 'POST',
      body: JSON.stringify(templateId ? { templateId } : {}),
    }),

  getHealthHistory: (buyerBusinessId: string) =>
    apiFetch<{ current: number; thirtyDaysAgo: number; sixtyDaysAgo: number; ninetyDaysAgo: number; trend: string }>(`/wholesale/customers/${buyerBusinessId}/health-history`),

  getMessageTemplates: () =>
    apiFetch<MessageTemplate[]>('/wholesale/message-templates'),

  createMessageTemplate: (data: { name: string; body: string }) =>
    apiFetch<MessageTemplate>('/wholesale/message-templates', { method: 'POST', body: JSON.stringify(data) }),

  updateMessageTemplate: (id: string, data: { name?: string; body?: string; sortOrder?: number }) =>
    apiFetch<MessageTemplate>(`/wholesale/message-templates/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  deleteMessageTemplate: (id: string) =>
    apiFetch<void>(`/wholesale/message-templates/${id}`, { method: 'DELETE' }),

  // CRM: Insights & Alerts (Sprint 4)
  getCrmSummary: () =>
    apiFetch<CrmSummary>('/wholesale/crm/summary'),

  getCrmAlerts: (params?: { status?: string; alertType?: string; skip?: number; take?: number }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.alertType) q.set('alertType', params.alertType);
    if (params?.skip) q.set('skip', String(params.skip));
    if (params?.take) q.set('take', String(params.take));
    const qs = q.toString();
    return apiFetch<{ data: CrmAlert[]; unreadCount: number }>(`/wholesale/crm/alerts${qs ? `?${qs}` : ''}`);
  },

  markAlertRead: (id: string) =>
    apiFetch<CrmAlert>(`/wholesale/crm/alerts/${id}/read`, { method: 'PATCH' }),

  markAllAlertsRead: () =>
    apiFetch<{ markedCount: number }>('/wholesale/crm/alerts/read-all', { method: 'PATCH' }),

  dismissAlert: (id: string) =>
    apiFetch<CrmAlert>(`/wholesale/crm/alerts/${id}/dismiss`, { method: 'PATCH' }),

  getDeliveryPrefs: (buyerBusinessId: string) =>
    apiFetch<DeliveryPrefs>(`/wholesale/customers/${buyerBusinessId}/delivery-prefs`),
  updateDeliveryPrefs: (buyerBusinessId: string, data: Partial<DeliveryPrefs>) =>
    apiFetch<DeliveryPrefs>(`/wholesale/customers/${buyerBusinessId}/delivery-prefs`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  getCrmSettings: () =>
    apiFetch<CrmSettings>('/wholesale/crm/settings'),

  updateCrmSettings: (data: Partial<CrmSettings>) =>
    apiFetch<CrmSettings>('/wholesale/crm/settings', { method: 'PATCH', body: JSON.stringify(data) }),

  getCrmHealthHistory: (buyerBusinessId: string, days?: number) =>
    apiFetch<HealthSnapshot[]>(`/wholesale/crm/buyers/${buyerBusinessId}/health-history${days ? `?days=${days}` : ''}`),

  getMySellers: () =>
    apiFetch<SellerSummary[]>('/wholesale/my-sellers'),
  getSellerProfile: (sellerBusinessId: string) =>
    apiFetch<SellerProfile>(`/wholesale/my-sellers/${sellerBusinessId}`),
  getFavorites: () =>
    apiFetch<FavoriteProduct[]>('/wholesale/favorites'),
  addFavorite: (productId: string) =>
    apiFetch<any>(`/wholesale/favorites/${productId}`, { method: 'POST' }),
  removeFavorite: (productId: string) =>
    apiFetch<any>(`/wholesale/favorites/${productId}`, { method: 'DELETE' }),
  getActiveNegotiations: () =>
    apiFetch<Negotiation[]>('/wholesale/negotiations'),
  getPaymentAnalytics: (buyerBusinessId: string) =>
    apiFetch<PaymentAnalytics>(`/wholesale/customers/${buyerBusinessId}/payment-analytics`),
  updateCreditLimit: (buyerBusinessId: string, creditLimit: number | null) =>
    apiFetch<any>(`/wholesale/customers/${buyerBusinessId}/credit-limit`, {
      method: 'PATCH',
      body: JSON.stringify({ creditLimit }),
    }),
  // Sprint 6
  getOrderTemplates: (sellerBusinessId: string) =>
    apiFetch<OrderTemplate[]>(`/wholesale/order-templates?sellerBusinessId=${sellerBusinessId}`),
  createOrderTemplate: (data: { sellerBusinessId: string; name: string; items: any[] }) =>
    apiFetch<OrderTemplate>('/wholesale/order-templates', { method: 'POST', body: JSON.stringify(data) }),
  updateOrderTemplate: (id: string, data: { name?: string; items?: any[] }) =>
    apiFetch<OrderTemplate>(`/wholesale/order-templates/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteOrderTemplate: (id: string) =>
    apiFetch<{ success: boolean }>(`/wholesale/order-templates/${id}`, { method: 'DELETE' }),
  getBuyerLtv: (buyerBusinessId: string) =>
    apiFetch<BuyerLtv>(`/wholesale/customers/${buyerBusinessId}/ltv`),
  sendBulkMessage: (segment: string, messageBody: string) =>
    apiFetch<{ sentCount: number }>('/wholesale/bulk-message', { method: 'POST', body: JSON.stringify({ segment, messageBody }) }),
  getOnboardingChecklist: (buyerBusinessId: string) =>
    apiFetch<OnboardingChecklist>(`/wholesale/customers/${buyerBusinessId}/onboarding`),
  markOnboardingStep: (buyerBusinessId: string, step: string) =>
    apiFetch<OnboardingChecklist>(`/wholesale/customers/${buyerBusinessId}/onboarding/${step}`, { method: 'POST' }),
  getProductAffinities: (buyerBusinessId?: string) =>
    apiFetch<ProductAffinity>(`/wholesale/product-affinity${buyerBusinessId ? `?buyerBusinessId=${buyerBusinessId}` : ''}`),
  getSeasonalPatterns: (buyerBusinessId: string) =>
    apiFetch<SeasonalPattern>(`/wholesale/customers/${buyerBusinessId}/seasonal`),
  getSuggestedActions: () =>
    apiFetch<SuggestedActionItem[]>('/wholesale/suggested-actions'),
  dismissSuggestedAction: (id: string) =>
    apiFetch<any>(`/wholesale/suggested-actions/${id}/dismiss`, { method: 'POST' }),
  executeSuggestedAction: (id: string) =>
    apiFetch<any>(`/wholesale/suggested-actions/${id}/execute`, { method: 'POST' }),
  getPlatformBenchmarks: () =>
    apiFetch<PlatformBenchmarks>('/wholesale/benchmarks'),
  getLosingBuyersReport: () =>
    apiFetch<LosingBuyer[]>('/wholesale/reports/losing-buyers'),
};
// Staging API: https://sweetspot-api-staging.onrender.com
// env fix 1773890733

// ── Spot assistant ──────────────────────────────────────────────────────
// SSE over POST (fetch body stream) — EventSource can't send a body, so we
// parse the frames ourselves. Reuses the same auth/business/impersonation
// headers as apiFetch; the proxy passes streams through (see route.ts).

export interface SpotEventHandlers {
  onStatus?: (t: string) => void;
  onStep?: (s: { name: string; ms: number; detail?: string }) => void;
  onDelta?: (t: string) => void;
  onReset?: () => void;
  onDone?: (d: { text: string; actions: any[]; vis: any[]; steps: any[]; quotaLeft?: number }) => void;
  onQueued?: (d: { id: string; ahead: number; via?: string; reason?: string }) => void;
  onError?: (e: { code?: string; message: string }) => void;
}

/**
 * Public ordering-site endpoints. Deliberately unauthenticated: a cafe
 * customer orders as a guest (docs/payments-consumer-cafe-plan.md §4), so
 * these must not send auth or business headers.
 */
export const siteApi = {
  trackEvent: (
    slug: string,
    eventType: 'site_view' | 'cta_click' | 'order_start',
    details: {
      ctaKind?: 'external_order' | 'cart' | 'add_to_order';
      productIds?: string[];
    } = {},
  ) =>
    fetch(`${API_URL}/storefront/business/${slug}/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ eventType, ...details }),
      cache: 'no-store',
      keepalive: true,
    }).then((res) => {
      if (!res.ok) throw new Error('Could not record storefront event');
      return res.json() as Promise<{ recorded: boolean }>;
    }),
  placeOrder: (
    slug: string,
    body: {
      items: Array<{ productId: string; quantity: number; selectedOptionIds?: string[] }>;
      fulfillment?: Record<string, unknown>;
      note?: string;
      guest?: { name: string; email: string; phone: string };
      paymentMethodId?: string;
    },
  ) =>
    fetch(`${API_URL}/storefront/business/${slug}/order`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      cache: 'no-store',
    }).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data as any)?.message || 'Could not place the order');
      return data as {
        checkoutGroupId: string;
        offerCount: number;
        orderToken: string;
        amount: string;
        paymentStatus: string;
        clientSecret?: string;
      };
    }),
  getOrder: (slug: string, token: string) =>
    fetch(`${API_URL}/storefront/business/${slug}/order/${token}`, { cache: 'no-store' }).then(async (res) => {
      if (!res.ok) throw new Error('Order not found');
      return res.json();
    }),
};

export const storefrontAnalyticsApi = {
  getDashboard: () => apiFetch<StorefrontAnalytics>('/storefront/analytics'),
};
export interface SpotWidgetValue {
  id: string;
  title: string;
  kind: 'stat' | 'list' | 'kv' | string;
  value?: string;
  rows?: [string, string][];
}

export const assistantApi = {
  /** Tier-A dashboard_widget / alert_custom ops — {add|update|remove}. */
  applyWidgetOp: (op: Record<string, unknown>) =>
    apiFetch<{ ok: boolean }>('/assistant/widgets/ops', { method: 'POST', body: JSON.stringify(op) }),
  applyAlertOp: (op: Record<string, unknown>) =>
    apiFetch<{ ok: boolean }>('/assistant/alerts/ops', { method: 'POST', body: JSON.stringify(op) }),
  getWidgetValues: () => apiFetch<{ widgets: SpotWidgetValue[] }>('/assistant/widgets/values'),
  async chatStream(
    q: string,
    history: { role: 'user' | 'assistant'; text: string }[],
    handlers: SpotEventHandlers,
    signal?: AbortSignal,
    /** Current pathname — lets Spot answer about what is on screen. */
    page?: string,
  ): Promise<void> {
    const authHeaders = await getAuthHeaders();
    let res: Response;
    try {
      res = await fetch(`${API_URL}/assistant/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
          ...getBusinessIdHeader(),
          ...getImpersonationHeader(),
        },
        body: JSON.stringify({ q, history, page }),
        signal,
        cache: 'no-store',
      });
    } catch (e: any) {
      if (e?.name !== 'AbortError') handlers.onError?.({ message: 'Network error — try again.' });
      return;
    }
    if (!res.ok || !res.body) {
      handlers.onError?.({ message: res.status === 401 ? 'Please sign in again.' : `Assistant unavailable (${res.status}).` });
      return;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    const dispatch = (frame: string) => {
      let event = 'message';
      const dataLines: string[] = [];
      for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
      }
      if (!dataLines.length) return;
      let data: any;
      try { data = JSON.parse(dataLines.join('\n')); } catch { return; }
      if (event === 'status') handlers.onStatus?.(data.t);
      else if (event === 'step') handlers.onStep?.(data);
      else if (event === 'delta') handlers.onDelta?.(data.t);
      else if (event === 'reset') handlers.onReset?.();
      else if (event === 'done') handlers.onDone?.(data);
      else if (event === 'queued') handlers.onQueued?.(data);
      else if (event === 'error') handlers.onError?.(data);
    };
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        for (;;) {
          const sep = buf.indexOf('\n\n');
          if (sep < 0) break;
          const frame = buf.slice(0, sep);
          buf = buf.slice(sep + 2);
          if (frame.trim() && !frame.startsWith(':')) dispatch(frame);
        }
      }
    } catch (e: any) {
      if (e?.name !== 'AbortError') handlers.onError?.({ message: 'Connection dropped — try again.' });
    }
  },
  relayResult: (id: string) =>
    apiFetch<{ status: string; text?: string; actions?: any[]; vis?: any[]; ahead?: number }>(`/assistant/relay/${id}`),
  updateNotificationPref: (eventType: string, enabled: boolean) =>
    apiFetch<any>('/notifications/preferences', {
      method: 'PATCH',
      body: JSON.stringify({ preferences: [{ eventType, enabled }] }),
    }),
};

export type InstagramAccountContext = { businessId: string; session: InstagramInboxSession | null; signal?: AbortSignal };
export type InstagramOwnedAccount = { connectionId: string; managementBusinessId: string; username: string; name: string | null;
  profilePictureUrl: string | null; status: string; tokenExpiresAt: string | null; lastSyncAt: string | null };
export type InstagramLinkedStore = { id: string; name: string; linked: boolean; isMessagesStore: boolean;
  siteHandle: string | null; marketplaceHandle: string | null; isDiscoverable: boolean; feedPromotionEnabled: boolean };
export type InstagramFeedSettings = { autoEnabled: boolean; defaultBusinessId: string | null };
export type InstagramFeedSelection = { postId: string; visibility: 'AUTO' | 'SHOW' | 'HIDE'; selected: boolean; businessIds: string[] };
const accountPath = (id: string) => `/instagram/accounts/${encodeURIComponent(id)}`;
const queryCursor = (cursor?: string) => cursor ? `?${new URLSearchParams({ cursor })}` : '';
const accountRequest = <T>(path: string, ctx: InstagramAccountContext, method = 'GET', body?: unknown) =>
  inboxFetch<T>(path, ctx.businessId, { signal: ctx.signal, method, ...(body === undefined ? {} : { body: JSON.stringify(body) }) }, ctx.session);
export const instagramAccountsApi = {
  forStore: (store: string, ctx: InstagramAccountContext, cursor?: string) => accountRequest<{ items: InstagramOwnedAccount[]; nextCursor: string | null }>(`/instagram/accounts/for-store/${encodeURIComponent(store)}${queryCursor(cursor)}`, ctx),
  list: (ctx: InstagramAccountContext, cursor?: string) => accountRequest<{ items: InstagramOwnedAccount[]; nextCursor: string | null }>(`/instagram/accounts${queryCursor(cursor)}`, ctx),
  stores: (id: string, ctx: InstagramAccountContext, cursor?: string) => accountRequest<{ items: InstagramLinkedStore[]; nextCursor: string | null }>(`${accountPath(id)}/stores${queryCursor(cursor)}`, ctx),
  link: (id: string, store: string, linked: boolean, ctx: InstagramAccountContext) => accountRequest<{ businessId: string; linked: boolean }>(`${accountPath(id)}/stores/${encodeURIComponent(store)}`, ctx, 'PUT', { linked }),
  status: (id: string, ctx: InstagramAccountContext) => accountRequest<InstagramConnectionStatus>(`${accountPath(id)}/status`, ctx),
  authorize: (id: string, ctx: InstagramAccountContext) => accountRequest<{ url: string }>(`${accountPath(id)}/authorize`, ctx, 'POST'),
  sync: (id: string, ctx: InstagramAccountContext) => accountRequest<{ imported: number; lastSyncAt: string | null }>(`${accountPath(id)}/sync`, ctx, 'POST'),
  disconnect: (id: string, ctx: InstagramAccountContext) => accountRequest<{ success: boolean }>(accountPath(id), ctx, 'DELETE'),
  settings: (id: string, ctx: InstagramAccountContext) => accountRequest<InstagramFeedSettings>(`${accountPath(id)}/feed`, ctx),
  updateSettings: (id: string, value: Partial<InstagramFeedSettings>, ctx: InstagramAccountContext) => accountRequest<InstagramFeedSettings>(`${accountPath(id)}/feed`, ctx, 'PUT', value),
  post: (id: string, post: string, value: { visibility?: InstagramFeedSelection['visibility']; businessIds?: string[] }, ctx: InstagramAccountContext) =>
    accountRequest<InstagramFeedSelection>(`${accountPath(id)}/feed/posts/${encodeURIComponent(post)}`, ctx, 'PUT', value),
  media: (id: string, store: string, ctx: InstagramAccountContext, cursor?: string, selectedId?: string | null) => {
    const query = new URLSearchParams(); if (cursor) query.set('cursor', cursor); if (selectedId) query.set('selectedId', selectedId);
    return accountRequest<InstagramMediaPage>(`${accountPath(id)}/stores/${encodeURIComponent(store)}/media${query.size ? `?${query}` : ''}`, ctx);
  },
  placement: (id: string, store: string, media: string, channel: 'storefront' | 'marketplace', approved: boolean, ctx: InstagramAccountContext) =>
    accountRequest<{ id: string; showOnStorefront: boolean; showOnMarketplace: boolean }>(`${accountPath(id)}/stores/${encodeURIComponent(store)}/media/${encodeURIComponent(media)}/${channel}`, ctx, 'PUT', { approved }),
  storePromotion: (store: string, ctx: InstagramAccountContext) => accountRequest<{ businessId: string; enabled: boolean; eligible: boolean }>(`/feed/stores/${encodeURIComponent(store)}/promotion`, ctx),
  updateStorePromotion: (store: string, enabled: boolean, ctx: InstagramAccountContext) => accountRequest<{ businessId: string; enabled: boolean }>(`/feed/stores/${encodeURIComponent(store)}/promotion`, ctx, 'PUT', { enabled }),
};

export type WebsiteSourcePhoto = { id: string; previewUrl: string | null; caption: string | null; permalink: string | null; username: string };
export type WebsiteDraftSummary = { id: string; templateId: string; templateRevision: number; revision: number; updatedAt: string; displayName: string };
export type WebsiteDraft = Omit<WebsiteDraftSummary, 'displayName'> & { doc: WebsiteLayout; photos: WebsiteSourcePhoto[]; sourceAvailable: boolean };
export type WebsiteApplied = { applied: true; siteConfig: Record<string, unknown>; siteSlug: string | null; sitePublished: boolean };
const websiteDraftPath = (id: string) => `/website-builder/drafts/${encodeURIComponent(id)}`;
export const websiteBuilderApi = {
  templates: (ctx: InstagramAccountContext) => accountRequest<{ items: WebsiteTemplate[] }>('/website-builder/templates', ctx),
  drafts: (ctx: InstagramAccountContext) => accountRequest<{ items: WebsiteDraftSummary[] }>('/website-builder/drafts', ctx),
  get: (id: string, ctx: InstagramAccountContext) => accountRequest<WebsiteDraft>(websiteDraftPath(id), ctx),
  create: (body: { templateId: string; connectionId?: string }, ctx: InstagramAccountContext) => accountRequest<WebsiteDraft>('/website-builder/drafts', ctx, 'POST', body),
  save: (id: string, revision: number, doc: WebsiteLayout, ctx: InstagramAccountContext) => accountRequest<WebsiteDraft>(websiteDraftPath(id), ctx, 'PATCH', { revision, doc }),
  remove: (id: string, revision: number, ctx: InstagramAccountContext) => accountRequest<{ deleted: true }>(websiteDraftPath(id), ctx, 'DELETE', { revision }),
  apply: (id: string, revision: number, approvePhotos: boolean, ctx: InstagramAccountContext) => accountRequest<WebsiteApplied>(`${websiteDraftPath(id)}/apply`, ctx, 'POST', { revision, approvePhotos }),
};
